import { useContext, useEffect, useState, useRef } from 'react';
import {
  Alert,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Container,
  styled,
  Tab,
  Tabs,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  EditOutlined,
  TranslateOutlined,
  VisibilityOutlined,
  ListAlt,
} from '@mui/icons-material';
import Sidebar from '../SideNavigationBar';
import { useLocation, useNavigate } from 'react-router-dom';
import Context from '../../context';
import { useGetToken } from '../../hooks/useGetToken';
import {
  Constants,
  fbGetForm,
  fbGetForms,
  fbGetFrontline,
  fbGetImages,
  fbGetParentForm,
  fbGetSections,
  fbPostForm,
  fbPutForm,
  fbPutSections,
  getNextQuestionSetId,
  getParentSetId,
  getTag,
  hasFormChanges,
  getLocalizedText,
  isFormValid,
} from '../../services/service';
import LoadingSpinner from '../LoadingSpinner';
import {
  ExternalDataMapping,
  ExternalDataSystem,
  ExternalDataTable,
  ExternalDataType,
  FbeCharacteristics,
  FbeCriteria,
  FbeForm,
  FbeFormFeature,
  fbeFormFields,
  FbeFrontline,
  FbeFrontlineInfo,
  FbeMode,
  FbeOption,
  FbeQuestion,
  FbeQuestionFeature,
  FbeRepeatType,
  FbeResultDefinitionFormat,
  FbeResultHandlingType,
  FbeResultSystemDestination,
  FbeResultWorkflowStatus,
  FbeSection,
  FbeSectionFeature,
  FbeType,
  FbeWorkflowStates,
  FbeWorkflowStatus,
  getCountryDisplayName,
  getLanguageDisplayName,
  InjectTime,
  LocalisedText,
  MappingType,
  TacoRole,
} from '../../services/fbeTypes';
import EditSection from '../EditSection';
import EditQuestion from '../EditQuestion';
import FormPreview from '../FormPreview';
import FormTranslationsTab from '../FormTranslationsTab';
import ConfirmDialog from '../ConfirmDialog';
import MultiSelect from '../MultiSelect';
import SingleSelect from '../SingleSelect';
import { activeWorkflowStatusOptions, criteriaOperatorOptions, externalTypeOperatorOptions, externalTypeOptions, featureOptions, formTypeOptions, formWorkflowTaskOptions, generateResultTriggerOptions, generateTriggerOptions, modeOptions, repeatTypeOptions, resultSystemDestinationOptions, roleOptions } from '../../services/options';
import InputField from '../InputField';
import FBCheckbox from '../FBCheckbox';
import LineAction, { LineActionType } from '../LineAction';
import InputRowContainer from '../InputRowContainer';
import MultiSelectFree from '../MultiSelectFree';
import EditContainer from '../EditContainer';
import FieldLock from '../FieldLock';
import RichTextField from '../RichTextField';
import { getUserGroups } from '../../utils/getUserGroups';

const REACT_APP_IES_LOCAL_ACCESS =
  process.env.VITE_IES_LOCAL_ACCESS || 'invalidLocal';
const REACT_APP_IES_GLOBAL_ACCESS =
  process.env.VITE_IES_GLOBAL_ACCESS || 'invalidGlobal';
const REACT_APP_TEMPLATE_VERSION =
  process.env.VITE_TEMPLATE_VERSION || '2.0.0';

  const enum CharacteristicType {
    CRITERIA_CHARACTERISTICS_AND = 0,
    CRITERIA_CHARACTERISTICS_OR = 1,
    CRITERIA_NETWORK_AND = 2,
    CRITERIA_NETWORK_OR = 3,
  }
// Styled components based on your styles
const TabContainerStyled = styled(Box)({
  paddingLeft: '24px',
  paddingRight: '24px',
});

export const SmallTabBoxStyleStyled = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  overflowX: 'auto',
});

export const SmallTabStyleStyled = styled(Tab)({
  minWidth: 'auto',
  width: 'auto',
  flex: 1,
});

const GapStyled = styled(Box)({
  gap: '16px',
});

const ButtonStyled = styled(Button)({
  borderRadius: '24px',
  padding: '6px 50px',
});

interface LocationState {
  formData: {
    pk: string;
    sk: string;
  };
}

const defaultFbeForm: FbeForm = {
  active: true,
  activeWorkflowStatus: [],
  appliedCountries: [],
  criteria: {},
  features: [],
  formId: '',
  mode: FbeMode.LIVE,
  modifiedAt: '',
  modifiedBy: '',
  name: [],
  order: 0,
  resultHandling:[
    {
      resultEmailReceivers: [],
      resultWorkflowStatus: [FbeResultWorkflowStatus.NONE],
      resultFormat: [],
      resultSystemDestination: [FbeResultSystemDestination.NONE]
    }
  ],
  role: [],
  workflowStatus: FbeWorkflowStatus.NONE,
  repeatType: FbeRepeatType.DIRECT,
  type: '' as unknown as FbeType,
  templateVersion: REACT_APP_TEMPLATE_VERSION
};

const fbGetSettingsErrorMessage = 'Unable to retrieve settings!';
const fbGetFormErrorMessage = 'Unable to retrieve the form!';
const fbGetImagesErrorMessage = 'Unable to retrieve images!';
const fbGetSectionsErrorMessage = 'Unable to retrieve sections!';
const fbPostFormErrorMessage = 'Unable to create the form!';
const fbPutFormErrorMessage =  'Unable to update the form!';
const fbPutSectionsErrorMessage = 'Unable to update the form sections!';

const fbGetSettingsSuccessMessage = 'Retrieved the settings!';
const fbGetFormSuccessMessage = 'Retrieved the form!';
const fbGetImagesSuccessMessage = 'Retrieved the images!';
const fbGetSectionsSuccessMessage = 'Retrieve sections!';
const fbPostFormSuccessMessage = 'Created the form!';
const fbPutFormSuccessMessage =  'Updated the form!';
const fbPutSectionsSuccessMessage = 'Updated the form sections!';

export default function EditForm(): JSX.Element {
  const [formResponse, setFormResponse] = useState<FbeForm>();
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [formSections, setFormSections] = useState<FbeSection[] | null>();
  const {
    authState,
    updateIsLoading,
    setShowError,
    setShowSuccess,
    setMessage
  } = useContext(Context);
  const { user, graph } = authState;
  const userGroups = getUserGroups(user?.idTokenClaims?.['groups'], graph?.groups);
  const isLocalAdmin = (userGroups as string[]).includes(REACT_APP_IES_LOCAL_ACCESS) &&
    !(userGroups as string[]).includes(REACT_APP_IES_GLOBAL_ACCESS);
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [readOnlyForm, setReadOnlyForm] = useState<boolean>(false);
  const [formState, setFormState] = useState<FbeForm>(defaultFbeForm);
  const [originalFormState, setOriginalFormState] =
    useState<FbeForm>(defaultFbeForm);
  const [smallTabIndex, setSmallTabIndex] = useState(0);
  const [translationDraft, setTranslationDraft] = useState<{lang?: string; name?: string; description?: string}>({});
  const [translationsDirty, setTranslationsDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(0);
  const [isExternalDataUpdated, setIsExternalDataUpdated] =
    useState<boolean>(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<
    number | null
  >(-1);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<
    number | null
  >(-1);
  const [sectionInitialTab, setSectionInitialTab] = useState<number>(0);
  const [questionInitialTab, setQuestionInitialTab] = useState<number>(0);
  const [isScoringEnabled, setIsScoringEnabled] = useState<boolean>();
  const [formValid, setFormValid] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const location = useLocation();
  const formData = location.state?.formData as LocationState['formData'];
  const [getTokenFunction] = useGetToken();
  // Pre-select Translations tab + language when coming from the FormCard translations submenu
  const initialTab = location.state?.tab === 'translations' ? 1 : 0;
  const [tabIndex, setTabIndex] = useState(initialTab);
  const [language, setLanguage] = useState<string>(location.state?.language || 'en');
  // Capture navigation language in a ref so effects always have a stable, non-stale reference.
  // All auto-correct setLanguage calls must skip when this is set.
  const navigationLanguageRef = useRef<string | undefined>(location.state?.language);
  const navigate = useNavigate();
  const isEditPath = location.pathname.includes('edit');

  // Tracks the version number at load time so we can detect if another user saves
  const loadedVersionRef = useRef<number | undefined>(undefined);
  const isPollingRef = useRef(false);
  const conflictBannerRef = useRef<{ modifiedByName?: string; modifiedAt?: string } | null>(null);
  const [conflictBanner, setConflictBanner] = useState<{ modifiedByName?: string; modifiedAt?: string } | null>(null);
  const [saveConflict, setSaveConflict] = useState<{ modifiedByName?: string } | null>(null);
  // Draft translations saved to sessionStorage before a conflict-blocked reload
  const [restoredDraft, setRestoredDraft] = useState<{ name: LocalisedText[]; descriptions: LocalisedText[] } | null>(null);
  const [touchedFields, setTouchedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [originalFormSections, setOriginalFormSections] = useState<
    FbeSection[] | null
  >();
  const [imageList, setImageList] = useState<string[]>([]);
  const [parentForm, setParentForm] = useState<FbeForm>(defaultFbeForm);
  const [parentSections, setParentSections] = useState<FbeSection[]>([]);
  const [frontline, setFrontline] = useState<FbeFrontline | null>(null);
  const [usedOrders, setUsedOrders] = useState<number[]>([]);
  const [allFormsList, setAllFormsList] = useState<FbeForm[]>([]);
  const [orderAnchorEl, setOrderAnchorEl] = useState<HTMLElement | null>(null);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>(['en']);
  const initialLanguageSetRef = useRef(false);

  useEffect(() => {
    const getImageList = async () => {
      try {
        updateIsLoading(true);
        const accessToken = await getTokenFunction();
        const imageListResults = await fbGetImages(
          accessToken
        );

        if (imageListResults) {
          setImageList(imageListResults);
          setMessage(fbGetImagesSuccessMessage);
          setShowSuccess(true);
        } else {
          setMessage(fbGetImagesErrorMessage)
          setShowError(true);
        }

        const settingsRes: FbeFrontline | null = await fbGetFrontline(
          accessToken
        );

        if(settingsRes){
          setFrontline(settingsRes);
          const info = settingsRes.settings[settingsRes.frontline as keyof typeof frontline] as unknown as FbeFrontlineInfo;
           setSupportedLanguages(
            info && info.languages && info.languages.length > 0
              ? [...info.languages].sort((a, b) =>
                  getLanguageDisplayName(a, settingsRes.settings.languageDisplayNames || {})
                    .localeCompare(getLanguageDisplayName(b, settingsRes.settings.languageDisplayNames || {}))
                )
              : ['en']
          );
          if(!navigationLanguageRef.current && formState && formState.name.length > 0){
            setLanguage(formState.name[0].language);
          } else if (!navigationLanguageRef.current) {
            const queryParams = new URLSearchParams(location.search);
            const createLanguage = queryParams.get('language');
            if(createLanguage){
              setLanguage(createLanguage)
            } else {
              if(formState && formState.name && formState.name.length > 0){
                setLanguage(formState.name[0].language);
              }
            }
          }

          setMessage(fbGetSettingsSuccessMessage);
          setShowSuccess(true);
        } else {
          setShowError(true);
          setMessage(fbGetSettingsErrorMessage);
        }
      } catch (e) {
        setMessage(`fbGetImages ${e}`)
        setShowError(true);
    } finally {
        updateIsLoading(false);
      }
    };

    getImageList();

    const queryParams = new URLSearchParams(location.search);
    const createLanguage = queryParams.get('language');
    if(createLanguage){
      setLanguage(createLanguage);
      if(!formState.pk){setFormState({...formState, name: [], defaultLanguage: createLanguage})}
    }
  }, []);

  // ── Poll for concurrent edits every 60s; also fires when user returns to tab ─
  useEffect(() => {
    if (!formResponse?.pk || !formResponse?.sk) return;
    const poll = async () => {
      if (document.visibilityState === 'hidden' || isPollingRef.current) return;
      isPollingRef.current = true;
      try {
        const token = await getTokenFunction();
        const latest = await fbGetForm(formResponse.pk || '', formResponse.sk || '', token);
        if (
          latest &&
          loadedVersionRef.current !== undefined &&
          (latest.version ?? 0) > loadedVersionRef.current &&
          !conflictBannerRef.current
        ) {
          const banner = {
            modifiedByName: latest.modifiedByName || latest.modifiedBy,
            modifiedAt: latest.modifiedAt,
          };
          conflictBannerRef.current = banner;
          setConflictBanner(banner);
        }
      } catch {
        // silently ignore poll failures
      } finally {
        isPollingRef.current = false;
      }
    };
    const interval = setInterval(poll, 60_000);
    document.addEventListener('visibilitychange', poll);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [formResponse?.pk, formResponse?.sk]);

  useEffect(() => {
    const getFormData = async () => {
      try {
        updateIsLoading(true);
        const accessToken = await getTokenFunction();

        const formRes: FbeForm | null = await fbGetForm(
          formData?.pk || formResponse?.pk || '',
          formData?.sk || formResponse?.sk || '',
          accessToken
        );
        if (formRes) {
          const form: FbeForm = {
            pk: formRes.pk,
            sk: formRes.sk,
            description: formRes.description,
            descriptions: formRes.descriptions,
            active: formRes.active,
            activeWorkflowStatus: formRes.activeWorkflowStatus,
            appliedCountries: formRes.appliedCountries,
            createdAt: formRes.createdAt,
            createdBy: formRes.createdBy,
            createdByName: formRes.createdByName,
            criteria: formRes.criteria,
            features: formRes.features,
            format: formRes.format,
            generationType: formRes.generationType,
            lockedFields: formRes.lockedFields ? formRes.lockedFields.sort() : formRes.lockedFields,
            mode: formRes.mode || FbeMode.LIVE,
            modifiedAt: formRes.modifiedAt,
            modifiedBy: formRes.modifiedBy,
            modifiedByName: formRes.modifiedByName,
            name: formRes.name,
            formId: formRes.formId,
            order: formRes.order,
            repeatType: formRes.repeatType,
            resultHandling: formRes.resultHandling,
            role: formRes.role,
            sourceDestination: formRes.sourceDestination,
            templateVersion: REACT_APP_TEMPLATE_VERSION,
            type: formRes.type,
            version: formRes.version,
            workflowTask: formRes.workflowTask,
            workflowStatus:
              formRes.workflowStatus === FbeWorkflowStatus.NONE
                ? ('none' as FbeWorkflowStatus)
                : formRes.workflowStatus,
            externalData: formRes.externalData,
            questionSetIdRange: formRes.questionSetIdRange,
          };

          if(formRes.passingScorePercentage){
            form.passingScorePercentage = formRes.passingScorePercentage
          }

          if(!form.externalData || form.externalData.length === 0){
            form.externalData = [
              {
                injectTime: InjectTime.GENERATION,
                system: ExternalDataSystem.IES,
                type: ExternalDataType.DYNAMO_DB,
                table: ExternalDataTable.CHARACTERISTICS,
                mapping: []
              },
              {
                injectTime: InjectTime.GENERATION,
                system: ExternalDataSystem.IES,
                type: ExternalDataType.DYNAMO_DB,
                table: ExternalDataTable.NETWORK,
                mapping: []
              }
            ]
          }

          if(form.externalData.length === 1){
            form.externalData.push({
              injectTime: InjectTime.GENERATION,
              system: ExternalDataSystem.IES,
              type: ExternalDataType.DYNAMO_DB,
              table: ExternalDataTable.NETWORK,
              mapping: []
            });
          }

          if(form.description === ''){
            delete form.description;
          }

          // Migrate legacy form field keys to workflowTask.
          if (!form.workflowTask) {
            const legacyForm = formRes as FbeForm & { workflowTaskType?: string; taskType?: string };
            form.workflowTask = legacyForm.workflowTaskType || legacyForm.taskType;
          }
          delete (form as FbeForm & { workflowTaskType?: string }).workflowTaskType;
          delete (form as FbeForm & { taskType?: string }).taskType;

          // Preserve the persisted backend defaultLanguage when present, and only
          // fall back to the first name entry's language if it is truly missing.
          if (!form.defaultLanguage && formRes?.defaultLanguage) {
            form.defaultLanguage = formRes.defaultLanguage;
          } else if (!form.defaultLanguage && form.name && form.name.length > 0) {
            form.defaultLanguage = form.name[0].language;
          }

          setFormState(JSON.parse(JSON.stringify(form)));
          setOriginalFormState(JSON.parse(JSON.stringify(form)));
          // Capture version at load time for concurrent-edit detection
          loadedVersionRef.current = form.version;

          // Restore draft translations that were saved before a conflict-blocked reload
          try {
            const storageKey = `fbb-draft-translations-${form.pk}-${form.sk}`;
            const savedDraft = sessionStorage.getItem(storageKey);
            if (savedDraft) {
              const draft = JSON.parse(savedDraft) as { name?: LocalisedText[]; descriptions?: LocalisedText[] };
              if (draft.name || draft.descriptions) {
                setRestoredDraft({ name: draft.name || [], descriptions: draft.descriptions || [] });
              }
              sessionStorage.removeItem(storageKey);
            }
          } catch { /* ignore storage errors */ }

          if(form.name.length > 0 && !navigationLanguageRef.current){
            setLanguage(form.name[0].language);
          }
          if (
            !formResponse ||
            JSON.stringify(formResponse) !== JSON.stringify(form)
          ) {
            const clonedForm = JSON.parse(JSON.stringify(form));
            setFormResponse(clonedForm);
          }

          const isGlobalFormForLocalAdmin = isLocalAdmin && form.pk === Constants.FORM_GLOBAL;
          setReadOnly(isGlobalFormForLocalAdmin);

          setReadOnlyForm(
            (isLocalAdmin && form.pk === Constants.FORM_GLOBAL)
//            (isLocalAdmin && form.type === FbeType.FULL_MERGE)
//            form.features.includes(FbeFormFeature.EXTEND_STATIC_FORM))
          );

          setIsScoringEnabled(form.passingScorePercentage ? true : false);

          let formSectionRes: FbeSection[] | null = await fbGetSections(
            formData?.pk || formResponse?.pk || '',
            formData?.sk || formResponse?.sk || '',
              accessToken
          );

          if(!formSectionRes){
            setShowError(true);
            setMessage(fbGetSectionsErrorMessage)
          } else {
            setMessage(fbGetSectionsSuccessMessage);
            setShowSuccess(true);
          }

          if(formSectionRes && formSectionRes.length > 0){
            formSectionRes = formSectionRes.map(formSection => {
              if(formSection.description === ''){
                delete formSection.description;
              }
              if(formSection.lockedFields && formSection.lockedFields.length > 0){
                formSection.lockedFields = formSection.lockedFields.sort();
              }

              formSection.questions = formSection.questions.map(question => {
                if(question.lockedFields && question.lockedFields.length > 0){
                  return {
                    ...question,
                    lockedFields: question.lockedFields.sort(),
                  }
                } else {
                  return question;
                }
              })
              return formSection;
            })
          }

          setFormSections(JSON.parse(JSON.stringify(formSectionRes)));
          setOriginalFormSections(JSON.parse(JSON.stringify(formSectionRes)));

          if(originalFormState.type === FbeType.MERGE){
            const parentFormRes = await fbGetParentForm(originalFormState.formId, accessToken);
            if(parentFormRes){
              setParentForm(parentFormRes);
              const parentSectionsRes = await fbGetSections(parentFormRes.pk || '', parentFormRes.sk || '', accessToken);
              if(parentSectionsRes){
                setParentSections(parentSectionsRes);
              }
            }
          }
          setShowSuccess(true);
          setMessage(fbGetFormSuccessMessage);
        } else {
          setShowError(true);
          setMessage(fbGetFormErrorMessage)
        }
      } catch (e) {
        setMessage(`fbGetForm/ fbGetSections ${e}`)
        setShowError(true);
      } finally {
        updateIsLoading(false);
      }
    };

    if( formResponse || (formData && formData.pk && formData.sk) ) {
      getFormData();
    }
  }, [formData, formResponse]);

  useEffect(() => {
    const isValid = isFormValid(formState, [])
    setFormValid(isValid);

    if(originalFormState.role) { originalFormState.role.sort(); }
    if(formState.role) { formState.role.sort(); }

    // Check if any changes were made
    const changedGeneralInfo = hasFormChanges(originalFormState,formState,(['name','active','mode','role','appliedCountries','order','formId','description','defaultLanguage','workflowTask'] as (keyof FbeForm)[]));
    const changedDetails = hasFormChanges(originalFormState,formState,['activeWorkflowStatus','features','workflowStatus','repeatType','type','questionSetIdRange'] as (keyof FbeForm)[]);
    const changedResults = hasFormChanges(originalFormState,formState,['resultHandling'] as (keyof FbeForm)[]);
    const changedScoring = hasFormChanges(originalFormState,formState,['passingScorePercentage'] as (keyof FbeForm)[]);
    const changedDependency = hasFormChanges(originalFormState,formState,['criteria'] as (keyof FbeForm)[]);

    const changedExternalData = hasFormChanges(originalFormState,formState,['externalData'] as (keyof FbeForm)[]);

    const isChanged = changedGeneralInfo ||
      changedDetails ||
      changedResults ||
      changedScoring ||
      changedDependency ||
      changedExternalData;

      setChangesMade(isChanged);
  }, [formState, originalFormState]);

  // On initial form load, set the top-right `language` to the form's primary language
  // but avoid overriding the user's explicit selection afterwards.
  useEffect(() => {
    if (navigationLanguageRef.current) {
      initialLanguageSetRef.current = true;
      return;
    }
    if (!initialLanguageSetRef.current && formState && formState.name && formState.name.length > 0) {
      const primary = formState.name[0].language;
      if (primary && primary !== language) {
        setLanguage(primary);
      }
      initialLanguageSetRef.current = true;
    }
  }, [formState, language]);

  const updateSidebarCode = '1';
  /*
  const [updateSidebarCode, setUpdateSidebarCode] = useState<number>(-1);
  useEffect(() => {
    setUpdateSidebarCode(new Date().getTime());
    console.error('update sidebar');
  }, [formSections, formState]);*/

  useEffect(() => {
    const updateFormExternalData = async () => {
      if (isExternalDataUpdated) {
        try {
          // Ensure workflowStatus and resultWorkflowStatus are consistent
          if (formState.workflowStatus === ('none' as unknown)) {
            formState.workflowStatus = FbeWorkflowStatus.NONE;
          }

          // Filter out externalData and mappings that are not present in formResponse
          const filteredExternalData = (formState.externalData || []).filter(
            (externalItem) => {
              const correspondingResponseItem = (
                formResponse?.externalData || []
              ).find(
                (responseItem) =>
                  responseItem.system === externalItem.system &&
                  responseItem.table === externalItem.table &&
                  responseItem.type === externalItem.type &&
                  responseItem.injectTime === externalItem.injectTime
              );

              if (!correspondingResponseItem) return false; // Remove if no corresponding externalData in formResponse

              // Filter mappings within each externalData
              externalItem.mapping = externalItem.mapping.filter(
                (mappingItem) =>
                  correspondingResponseItem.mapping.some(
                    (responseMapping) =>
                      responseMapping.compareValue ===
                        mappingItem.compareValue &&
                      responseMapping.source === mappingItem.source &&
                      responseMapping.dest === mappingItem.dest &&
                      responseMapping.type === mappingItem.type &&
                      responseMapping.operator === mappingItem.operator
                  )
              );

              return true; // Keep externalData if at least one mapping matches
            }
          );

          // Update formState with filtered externalData
          const updatedFormState = {
            ...formState,
            externalData:
              filteredExternalData.length > 0
                ? filteredExternalData
                : undefined,
          };

          const accessToken = await getTokenFunction();
          updateIsLoading(true);
          const response = await fbPutForm(updatedFormState, accessToken);

          if (response) {
            const savedForm = JSON.parse(JSON.stringify(response)) as FbeForm & { workflowTaskType?: string; taskType?: string };
            savedForm.workflowTask =
              savedForm.workflowTask ?? savedForm.workflowTaskType ?? savedForm.taskType ?? updatedFormState.workflowTask;
            delete savedForm.workflowTaskType;
            delete savedForm.taskType;
            setFormResponse(savedForm);
            setOriginalFormState(JSON.parse(JSON.stringify(savedForm)));
            setFormState(JSON.parse(JSON.stringify(savedForm)));
            loadedVersionRef.current = response.version;
            conflictBannerRef.current = null;
            setConflictBanner(null);
            setMessage(fbPutFormSuccessMessage);
            setShowSuccess(true);
          } else {
            setShowError(true);
            setMessage(fbPutFormErrorMessage)
          }
        } catch (e) {
          setMessage(`fbPutForm ${e}`)
          setShowError(true);
        } finally {
          updateIsLoading(false);
          setIsExternalDataUpdated(false); // Reset the flag
        }
      }
    };

    if (isExternalDataUpdated) {
      updateFormExternalData();
    }
  }, [isExternalDataUpdated, formState]);

  // Sync language with supportedLanguages (only auto-correct, don't override saved form data)
  useEffect(() => {
    // Never correct when navigated here with an explicit language — use the ref
    // (not location.state) to avoid stale closure issues in this effect.
    if (navigationLanguageRef.current) return;
    if (supportedLanguages.length > 0 && language && !supportedLanguages.includes(language)) {
      setLanguage(supportedLanguages[0]);
    }
  }, [supportedLanguages]);

  const receiversOptions = [
  {
    value: 'none',
    label: 'None',
    key: 'role',
    disabled: false,
  },
  ];

  const updateCharacteristicList = (
    list: FbeCharacteristics[],
    index: number,
    value: string,
  ): FbeCharacteristics[] => {
    const newValue =`${value}`;
    if(newValue.startsWith('S')){
      return list;
    }

    if(!list[index]){
      list[index] = {
        characteristicName: '',
        characteristicValue: ''
      }
    }

    if(newValue.startsWith('C')){
      const operatorValue = newValue.substring(1);
      if(operatorValue === ''){
        delete list[index].operator;
      } else {
        list[index].operator = operatorValue;
      }
      return list;
    }

    const isNameValue = newValue.startsWith('A');

    if(isNameValue){
      list[index].characteristicName = newValue.substring(1);
    } else {
      list[index].characteristicValue = newValue.substring(1);
    }
    return list;
  }

  const handleSetIsScoringEnabled = (
    checked: boolean
  ) => {
    setIsScoringEnabled(checked);
    if(!checked){
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {passingScorePercentage, ...prev } = formState;
      setFormState(prev);
    } else {

      const {passingScorePercentage, ...prev } = formState;
      if(!passingScorePercentage){
        setFormState({...prev, passingScorePercentage: 0});
      }
    }
  }

  const handleAddResultHandling = (
  ) => {
    const updateFormState = JSON.parse(JSON.stringify(formState)) as FbeForm;
    updateFormState.resultHandling.push({
      resultEmailReceivers: [FbeResultSystemDestination.NONE],
      resultFormat: [FbeResultDefinitionFormat.PDF],
      resultSystemDestination: [FbeResultSystemDestination.NONE],
      resultWorkflowStatus: [FbeResultWorkflowStatus.NONE],
    });

    setFormState(updateFormState);
  }

  const handleRemoveResultHandling = (
    index: number
  ) => {
    if(index === 0){ return };

    const updateFormState = JSON.parse(JSON.stringify(formState)) as FbeForm;
    if(index < updateFormState.resultHandling.length ){
      updateFormState.resultHandling.splice(index,1);
    }

    setFormState(updateFormState);
  }

  const handleResultHandlingChange = (
    field: keyof FbeResultHandlingType,
    value: unknown,
    externalIndex: number,
  ) => {
    const updateFormState = JSON.parse(JSON.stringify(formState)) as FbeForm;

    switch(field){
      case 'resultEmailReceivers':
        if(updateFormState.resultHandling[externalIndex].resultEmailReceivers.includes('none')){
          updateFormState.resultHandling[externalIndex].resultEmailReceivers = value as string[];
          updateFormState.resultHandling[externalIndex].resultEmailReceivers = updateFormState.resultHandling[externalIndex].resultEmailReceivers.filter(value => value !== 'none');
        } else {
          updateFormState.resultHandling[externalIndex].resultEmailReceivers = value as string[];
          if(updateFormState.resultHandling[externalIndex].resultEmailReceivers.includes('none')){
            updateFormState.resultHandling[externalIndex].resultEmailReceivers = ['none'];
          }
        }
        break;
      case 'resultFormat':
        updateFormState.resultHandling[externalIndex].resultFormat = value as FbeResultDefinitionFormat[];
        break;
      case 'resultSystemDestination':
        if(updateFormState.resultHandling[externalIndex].resultSystemDestination.includes(FbeResultSystemDestination.NONE)){
          updateFormState.resultHandling[externalIndex].resultSystemDestination = value as FbeResultSystemDestination[];
          updateFormState.resultHandling[externalIndex].resultSystemDestination = updateFormState.resultHandling[externalIndex].resultSystemDestination.filter(value => value !== FbeResultSystemDestination.NONE);
        } else {
          updateFormState.resultHandling[externalIndex].resultSystemDestination = value as FbeResultSystemDestination[];
          if(updateFormState.resultHandling[externalIndex].resultSystemDestination.includes(FbeResultSystemDestination.NONE)){
            updateFormState.resultHandling[externalIndex].resultSystemDestination = [FbeResultSystemDestination.NONE];
          }
        }
        break;
      case 'resultWorkflowStatus':
        if(updateFormState.resultHandling[externalIndex].resultWorkflowStatus.includes(FbeResultWorkflowStatus.NONE)){
          updateFormState.resultHandling[externalIndex].resultWorkflowStatus = value as FbeResultWorkflowStatus[];
          updateFormState.resultHandling[externalIndex].resultWorkflowStatus = updateFormState.resultHandling[externalIndex].resultWorkflowStatus.filter(value => value !== FbeResultWorkflowStatus.NONE);
        } else {
          updateFormState.resultHandling[externalIndex].resultWorkflowStatus = value as FbeResultWorkflowStatus[];
          if(updateFormState.resultHandling[externalIndex].resultWorkflowStatus.includes(FbeResultWorkflowStatus.NONE)){
            updateFormState.resultHandling[externalIndex].resultWorkflowStatus = [FbeResultWorkflowStatus.NONE];
          }
        }
        break;
    }

    setFormState(updateFormState);
  }

  const handleAllLockedFields = (
    locked: boolean
  ) => {
    const { lockedFields, ...prev} = formState;
    let updatedLockedFields = lockedFields || [];
    if(locked){
      updatedLockedFields = JSON.parse(JSON.stringify(fbeFormFields)) as string[];
    } else {
      updatedLockedFields = [];
    }

    if(updatedLockedFields.length > 0){
      setFormState({...prev, lockedFields: updatedLockedFields});
    } else {
      setFormState(prev);
    }
  }

  const handleLockedFields = (
    field: string,
    locked: boolean
  ) => {
    const { lockedFields, ...prev} = formState;
    let updatedLockedFields = lockedFields || [];
    if(locked){
      updatedLockedFields.push(field);
    } else {
      updatedLockedFields = updatedLockedFields.filter(updatedLockedField => updatedLockedField !== field);
    }

    updatedLockedFields = Array.from(new Set(updatedLockedFields));

    if(updatedLockedFields.length > 0){
      setFormState({...prev, lockedFields: updatedLockedFields.sort()});
    } else {
      setFormState(prev);
    }
  }

  const handleChange = (
    field: keyof FbeForm,
    value: unknown,
    language = 'en',
    externalIndex?: number,
    mappingIndex?: number,
    mappingField?: keyof ExternalDataMapping
  ) => {
    setFormState((prev: FbeForm) => {
      if (field === 'name') {
        const updatedName = prev.name;

        if(prev.name.length === 0){
          updatedName.push({
            language,
            text: value as string
          });
        } else {
          updatedName[0].text = value as string;
        }
        return { ...prev, [field]: updatedName };
      }

      if (field === 'formId') {
        return { ...prev, formId: `${value}`.toLowerCase().replaceAll(' ','_') };
      }

      if (field === 'description') {
        const updateForm = { ...prev, description: value as string} as FbeForm;
        const strippedContent = (updateForm.description || '')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\u00a0/g, ' ')
          .trim();
        if(strippedContent === ''){
          delete updateForm.description;
        }
        return updateForm;
      }

      if (field === 'workflowTask') {
        const updatedForm = { ...prev, workflowTask: value as string } as FbeForm & { workflowTaskType?: string; taskType?: string };
        delete updatedForm.workflowTaskType;
        delete updatedForm.taskType;
        return updatedForm;
      }

      if (field === 'passingScorePercentage') {
        let updatedPercentage = Number(value as number)

        if(updatedPercentage < 0) { updatedPercentage = 0 }
        if(updatedPercentage > 100) { updatedPercentage = 100 }

        return { ...prev, [field]: updatedPercentage };
      }

      if (field === 'role' && Array.isArray(prev[field])) {
        const updatedArray = value as TacoRole[];
        return { ...prev, [field]: updatedArray };
      }

      if (field === 'appliedCountries' && Array.isArray(prev[field])) {
        const updatedArray = value as string[];
        return { ...prev, [field]: updatedArray };
      }

      if (field === 'features' && Array.isArray(prev[field])) {
        const newFeatures = value as FbeFormFeature[];
        const oldFeatures = prev.features || [];
        // Detect which features were added
        const removalsAdded = newFeatures.includes(FbeFormFeature.ALLOW_REMOVALS_BY_SPV) &&
                             !oldFeatures.includes(FbeFormFeature.ALLOW_REMOVALS_BY_SPV);
        const reorderingAdded = newFeatures.includes(FbeFormFeature.ALLOW_REORDERING_BY_SPV) &&
                               !oldFeatures.includes(FbeFormFeature.ALLOW_REORDERING_BY_SPV);
        // Detect which features were removed
        const removalsRemoved = !newFeatures.includes(FbeFormFeature.ALLOW_REMOVALS_BY_SPV) &&
                               oldFeatures.includes(FbeFormFeature.ALLOW_REMOVALS_BY_SPV);
        const reorderingRemoved = !newFeatures.includes(FbeFormFeature.ALLOW_REORDERING_BY_SPV) &&
                                 oldFeatures.includes(FbeFormFeature.ALLOW_REORDERING_BY_SPV);
        // Cascade to sections and questions
        if (removalsAdded || removalsRemoved || reorderingAdded || reorderingRemoved) {
          const applyCascade = (sections: FbeSection[], originalSections: FbeSection[] | null) => {
            return sections.map((section, sectionIndex) => {
              const originalSection = originalSections && originalSections[sectionIndex];
              let sectionFeatures = section.features || [];
              let sectionModified = false;
              // Handle additions: Add all newly-added features in one pass
              if (removalsAdded && !sectionFeatures.includes(FbeSectionFeature.ALLOW_REMOVALS_BY_SPV)) {
                sectionFeatures = [...sectionFeatures, FbeSectionFeature.ALLOW_REMOVALS_BY_SPV];
                sectionModified = true;
              }
              if (reorderingAdded && !sectionFeatures.includes(FbeSectionFeature.ALLOW_REORDERING_BY_SPV)) {
                sectionFeatures = [...sectionFeatures, FbeSectionFeature.ALLOW_REORDERING_BY_SPV];
                sectionModified = true;
              }
              // Handle removals: Remove all removed features in one pass
              if (removalsRemoved && sectionFeatures.includes(FbeSectionFeature.ALLOW_REMOVALS_BY_SPV)) {
                sectionFeatures = sectionFeatures.filter(f => f !== FbeSectionFeature.ALLOW_REMOVALS_BY_SPV);
                sectionModified = true;
              }
              if (reorderingRemoved && sectionFeatures.includes(FbeSectionFeature.ALLOW_REORDERING_BY_SPV)) {
                sectionFeatures = sectionFeatures.filter(f => f !== FbeSectionFeature.ALLOW_REORDERING_BY_SPV);
                sectionModified = true;
              }
              // If section wasn't modified, return it unchanged
              if (!sectionModified) {
                return section;
              }
              // Check if after cascade, section features match original - if so, try to restore original
              const finalSectionFeatures = sectionFeatures.length > 0 ? sectionFeatures : undefined;
              const originalSectionFeatures = originalSection?.features;
              const featuresMatchOriginal = JSON.stringify(finalSectionFeatures) === JSON.stringify(originalSectionFeatures);
              // Cascade to questions (Form → Section → Question)
              const updatedQuestions = section.questions.map((question: FbeQuestion, questionIndex: number) => {
                const originalQuestion = originalSection?.questions?.[questionIndex];
                let questionFeatures = question.features || [];
                let questionModified = false;
                // Handle additions: Add all newly-added features in one pass
                if (removalsAdded && !questionFeatures.includes(FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV)) {
                  questionFeatures = [...questionFeatures, FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV];
                  questionModified = true;
                }
                if (reorderingAdded && !questionFeatures.includes(FbeQuestionFeature.ALLOW_REORDERING_BY_SPV)) {
                  questionFeatures = [...questionFeatures, FbeQuestionFeature.ALLOW_REORDERING_BY_SPV];
                  questionModified = true;
                }
                // Handle removals: Remove all removed features in one pass
                if (removalsRemoved && questionFeatures.includes(FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV)) {
                  questionFeatures = questionFeatures.filter(f => f !== FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV);
                  questionModified = true;
                }
                if (reorderingRemoved && questionFeatures.includes(FbeQuestionFeature.ALLOW_REORDERING_BY_SPV)) {
                  questionFeatures = questionFeatures.filter(f => f !== FbeQuestionFeature.ALLOW_REORDERING_BY_SPV);
                  questionModified = true;
                }
                // Only create new object if modified
                if (!questionModified) {
                  return question;
                }
                // Check if question features match original after cascade
                const finalQuestionFeatures = questionFeatures.length > 0 ? questionFeatures : undefined;
                const originalQuestionFeatures = originalQuestion?.features;
                // If features match original, return the original question to preserve reference
                if (originalQuestion && JSON.stringify(finalQuestionFeatures) === JSON.stringify(originalQuestionFeatures)) {
                  return originalQuestion;
                }
                return {
                  ...question,
                  features: finalQuestionFeatures
                };
              });
              // If both section features and all questions match original, return original section
              if (featuresMatchOriginal && originalSection) {
                const allQuestionsMatchOriginal = updatedQuestions.every((q, idx) => q === originalSection.questions?.[idx]);
                if (allQuestionsMatchOriginal) {
                  return originalSection;
                }
              }
              return {
                ...section,
                features: finalSectionFeatures,
                questions: updatedQuestions
              };
            });
          };

          // Apply cascade to current sections
          // Yellow will show if result differs from original (saved) state
          setFormSections((prevSections) => {
            if (!prevSections) return prevSections;
            return applyCascade(prevSections, originalFormSections || null);
          });
        }
      return { ...prev, [field]: newFeatures };
      }

      if (field === 'activeWorkflowStatus' && Array.isArray(prev[field])) {
        const updatedArray = value as FbeWorkflowStates[];
        return { ...prev, [field]: updatedArray };
      }

      if (
        field === 'criteria'
      ){
        switch(externalIndex){
          case CharacteristicType.CRITERIA_CHARACTERISTICS_AND:
            return { ...prev, criteria: {
              ...prev.criteria,
              characteristics: {
              ...prev.criteria.characteristics,
              and: updateCharacteristicList(prev.criteria.characteristics?.and||[], mappingIndex || 0,`${value}`)}
            }}
          case CharacteristicType.CRITERIA_CHARACTERISTICS_OR:
            return { ...prev, criteria: {
              ...prev.criteria,
              characteristics: {
              ...prev.criteria.characteristics,
              or: updateCharacteristicList(prev.criteria.characteristics?.or||[], mappingIndex || 0,`${value}`)}
            }}
          case CharacteristicType.CRITERIA_NETWORK_AND:
            return { ...prev, criteria: {
              ...prev.criteria,
              networkCharacteristics: {
              ...prev.criteria.networkCharacteristics,
              and: updateCharacteristicList(prev.criteria.networkCharacteristics?.and||[], mappingIndex || 0,`${value}`)}
            }}
          case CharacteristicType.CRITERIA_NETWORK_OR:
            return { ...prev, criteria: {
              ...prev.criteria,
              networkCharacteristics: {
              ...prev.criteria.networkCharacteristics,
              or: updateCharacteristicList(prev.criteria.networkCharacteristics?.or||[], mappingIndex || 0,`${value}`)}
            }}
          default:
            return { ...prev};
        }
      }
      if (
        field === 'externalData' &&
        externalIndex !== undefined &&
        mappingIndex !== undefined &&
        mappingField
      ) {
        const updatedExternalData = [...(prev.externalData || [])];
        const updatedMapping = [...updatedExternalData[externalIndex].mapping];
        updatedMapping[mappingIndex] = {
          ...updatedMapping[mappingIndex],
          [mappingField]: value,
        };

        if(mappingField==='operator' && (value as string) === ''){
          delete updatedMapping[mappingIndex].operator;
        } else if(mappingField==='compareValue' && (value as string) === ''){
          delete updatedMapping[mappingIndex].compareValue;
        } else if(mappingField==='default' && (value as string) === ''){
          delete updatedMapping[mappingIndex].default;
        } else if(mappingField==='value' && (value as string) === ''){
          delete updatedMapping[mappingIndex].value;
        } else if(mappingField==='isFormLevelUsage' && value === false){
          delete updatedMapping[mappingIndex].isFormLevelUsage;
        }

        updatedExternalData[externalIndex] = {
          ...updatedExternalData[externalIndex],
          mapping: updatedMapping,
          injectTime: InjectTime.GENERATION,
          system: ExternalDataSystem.IES,
          type: ExternalDataType.DYNAMO_DB,
        };

        return { ...prev, externalData: updatedExternalData };
      }

      return { ...prev, [field]: value };
    });
  };

  const handleListItemClick = (index: number) => {
    setSelectedIndex(index);
    setSelectedSectionIndex(null);
    setSelectedQuestionIndex(null);
  };

  const handleOpenOrderList = (e: any) => {
    setOrderAnchorEl(e.currentTarget);
  };

  const handleCloseOrderList = () => {
    setOrderAnchorEl(null);
  };

  useEffect(() => {
    const fetchUsedOrders = async () => {
      try {
        const token = await getTokenFunction();
        const allForms = await fbGetForms(token);
        if (allForms && allForms.length > 0) {
          const currentPk = formState?.pk || formResponse?.pk || '';
          // Include forms for the current frontline PK and global forms
          const filtered = allForms.filter((f) => {
            const pk = (f.pk || '');
            return pk === currentPk || pk === Constants.FORM_GLOBAL;
          });
          setAllFormsList(filtered);
          const orders = Array.from(new Set(filtered.map((f) => Number(f.order)).filter((n) => !Number.isNaN(n))));
          orders.sort((a, b) => a - b);
          setUsedOrders(orders);
        } else {
          setUsedOrders([]);
          setAllFormsList([]);
        }
      } catch (e) {
        setUsedOrders([]);
        setAllFormsList([]);
        console.error('Error fetching used orders:', e);
      }
    };

    // Fetch when frontline or form pk changes
    fetchUsedOrders();
  }, [formState?.pk, formResponse?.pk, frontline]);

  const handleSectionListItemClick = (index: number) => {
    setSelectedSectionIndex(index);
    setSectionInitialTab(0);
    setSelectedIndex(null);
    setSelectedQuestionIndex(null); // Reset question selection
  };

  const handleQuestionListItemClick = (
    sectionIndex: number,
    questionIndex: number
  ) => {
    setSelectedSectionIndex(sectionIndex);
    setSelectedIndex(null);
    setQuestionInitialTab(0);
    setSelectedQuestionIndex(questionIndex);
  };

  const handleTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    // If leaving Translations tab (index 1) and there are unsaved translation drafts, confirm
    if (tabIndex === 1 && newIndex !== 1 && translationsDirty) {
      setPendingTab(newIndex);
      setConfirmOpen(true);
      return;
    }

    setTabIndex(newIndex);
  };

  const handleSmallTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    setSmallTabIndex(newIndex);
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    setLanguage(event.target.value as string);
  };

  const handleDuplicateSection = (sectionIndex: number) => {
    if(formSections && formSections.length > 0  && sectionIndex < formSections.length){
      const newSection = JSON.parse(JSON.stringify(formSections[sectionIndex])) as FbeSection;
      newSection.order = formSections.length;
      newSection.sk = `${newSection.order}`;
      newSection.name[0].text = `duplicate ${newSection.name[0].text}`;
      if(newSection.mergeMetaData){
        newSection.mergeMetaData.questionSetId = getNextQuestionSetId(
          formResponse || {} as FbeForm,
          formSections && formSections?.length > 0 ? formSections.length : 0
        );
        newSection.mergeMetaData.description = newSection.name;
        newSection.mergeMetaData.tag = getTag(formResponse || {} as FbeForm, newSection);
      }
      const updatedSections = [
        ...(formSections || []),
        newSection
      ];
      setFormSections(updatedSections);
      setSelectedSectionIndex(updatedSections.length - 1); // Set index to the new section
      setSelectedQuestionIndex(null);
    }
  }

  const handleDuplicateQuestion = (sectionIndex: number, questionIndex: number) => {
    console.error(`handleDuplicateQuestion ${sectionIndex} ${questionIndex}`);
    const newSections = JSON.parse(JSON.stringify(formSections)) as FbeSection[];

    if(sectionIndex < newSections.length){
      const newQuestion = JSON.parse(JSON.stringify( newSections[sectionIndex].questions[questionIndex])) as FbeQuestion;
      newQuestion.order = newSections[sectionIndex].questions.length;
      newQuestion.question[0].text = `duplicate ${newQuestion.question[0].text}`;
      newSections[sectionIndex].questions = [...newSections[sectionIndex].questions, newQuestion];
      setFormSections(newSections);
      setSelectedSectionIndex(sectionIndex); // Set index to the new section
      setSelectedQuestionIndex(newSections[sectionIndex].questions.length - 1);
    }

  }

  const handleAddNewSection = (newSection: FbeSection) => {
    let newSectionWithMergeData: FbeSection;
    if (formResponse) {
      console.warn('isLocalAdmin', isLocalAdmin);
      newSectionWithMergeData = {
        ...newSection,
        mergeMetaData: {
          questionSetId: getNextQuestionSetId(
            formResponse,
            formSections && formSections?.length > 0 ? formSections.length : 0
          ),
          parentSetId: getParentSetId(formResponse),
          tacoRole: formResponse.role,
          description: newSection.name,
          tag: getTag(formResponse, newSection),
        },
      };
    } else {
      newSectionWithMergeData = {
        ...newSection,
      };
    }

    const updatedSections = [
      ...(formSections || []),
      newSectionWithMergeData
    ];
    setFormSections(updatedSections);
    setSelectedSectionIndex(updatedSections.length - 1); // Set index to the new section
    setSelectedQuestionIndex(null);
  };

  const filterResultTriggers = (
    _element: FbeResultHandlingType,
    index: number
  ): FbeOption[] => {
    const optionList: FbeOption[] = [];

    let usedOptions = '';
    let loopIndex = 0;

    formState.resultHandling.map(result => {
      const statuses = Array.isArray(result.resultWorkflowStatus)
        ? result.resultWorkflowStatus
        : [result.resultWorkflowStatus];
      statuses.forEach(status => {
        usedOptions += `;${loopIndex}=${status};`
      });
      loopIndex++;
    })

    generateResultTriggerOptions.map(option => {
      if(usedOptions.includes(option.value)){
        if(usedOptions.includes(`${index}=${option.value}`)){
          optionList.push(option);
        } else {
          optionList.push({
            ...option,
          disabled: true
        });

        }
      } else {
        optionList.push(option);
      }
    });

    return optionList;
}

    const handleAddNewQuestion = (
    sectionIndex: number,
    newQuestion: FbeQuestion
  ) => {
    const newSections = JSON.parse(JSON.stringify(formSections)) as FbeSection[];

    if(sectionIndex < newSections.length){
      newSections[sectionIndex].questions = [...newSections[sectionIndex].questions, newQuestion];
      setFormSections(newSections);
      setSelectedSectionIndex(sectionIndex); // Set index to the new section
      setSelectedQuestionIndex(newSections[sectionIndex].questions.length - 1);
    }

  };

  const handleUpdateQuestion = (updatedQuestion: FbeQuestion) => {
    setFormSections((prevSections) => {
      if (!prevSections) return [];

      return prevSections.map((section, secIndex) => {
        if (secIndex === selectedSectionIndex) {
          return {
            ...section,
            questions: section.questions.map((q, qIndex) =>
              qIndex === selectedQuestionIndex ? updatedQuestion : q
            ),
          };
        }
        return section;
      });
    });
  };

  const handleDeleteSection = async (index: number) => {
    if (formSections) {
      const updatedSections: FbeSection[] = formSections?.filter(
        (_, i) => i !== index
      );
      if (!formSections[index].pk) {
        setSelectedSectionIndex(updatedSections.length > 0 ? 0 : null);
        setFormSections(updatedSections);
      } else {
        const accessToken = await getTokenFunction();
        try {
          const finalUpdatedSections = updatedSections.filter(
            (section) => section.pk
          );
          updateIsLoading(true);
          if (formResponse) {
            const response = await fbPutSections(
              formResponse,
              finalUpdatedSections,
              accessToken
            );
            if (response) {
              setSelectedSectionIndex(updatedSections.length > 0 ? 0 : null);
              setOriginalFormSections(JSON.parse(JSON.stringify(updatedSections)));
              setFormSections(JSON.parse(JSON.stringify(updatedSections)));
              setMessage(fbPutSectionsSuccessMessage);
              setShowSuccess(true);
            } else {
              setShowError(true);
              setMessage(fbPutSectionsErrorMessage)
            }
          }
        } catch (e) {
          setMessage(`fbPutSections ${e}`)
          setShowError(true);
        } finally {
          updateIsLoading(false);
        }
      }
    }
  };

  const handleDeleteQuestion = async (
    sectionIndex: number,
    questionIndex: number
  ) => {
    if (formSections) {
      const updatedSections = [...formSections];
      const updatedQuestions = updatedSections[sectionIndex].questions.filter(
        (_, qIndex) => qIndex !== questionIndex
      );
      updatedSections[sectionIndex] = {
        ...updatedSections[sectionIndex],
        questions: updatedQuestions,
      };
      if (
        formResponse &&
        formSections[sectionIndex].questions[questionIndex].questionId
      ) {
        const accessToken = await getTokenFunction();
        try {
          const finalUpdatedSections = updatedSections.map((section) => ({
            ...section,
            questions: section.questions.filter((q) => q.questionId),
          }));
          updateIsLoading(true);
          const response = await fbPutSections(
            formResponse,
            finalUpdatedSections,
            accessToken
          );
          if (response) {
            setSelectedQuestionIndex(
              updatedSections[sectionIndex].questions.length > 0 ? 0 : null
            );
            setOriginalFormSections(JSON.parse(JSON.stringify(updatedSections)));
            setFormSections(JSON.parse(JSON.stringify(updatedSections)));
            setMessage(fbPutSectionsSuccessMessage);
            setShowSuccess(true);
        } else {
            setShowError(true);
            setMessage(fbPutSectionsErrorMessage)
          }
        } catch (e) {
          setMessage(`fbPutSections ${e}`)
          setShowError(true);
        } finally {
          updateIsLoading(false);
        }
      } else {
        setSelectedQuestionIndex(
          updatedSections[sectionIndex].questions.length > 0 ? 0 : null
        );
        setFormSections(updatedSections);
      }
    }
  };

  const handleReorderSections = (newOrder: FbeSection[], sectionIndex: number) => {
    const newSections = JSON.parse(JSON.stringify(newOrder)) as FbeSection[];
    const updatedSections = newSections.map( (newSection, index) => {
      return {
        ...newSection,
        internalCode:`question_group_${formState.pk}_${formState.sk}_${index + 1}`
      }
    });
    setFormSections(updatedSections);
    setSelectedSectionIndex(sectionIndex);
  };

  const handleReorderQuestions = (newOrder: FbeSection[], sectionIndex: number, questionIndex: number) => {
      // reset questionIds
      const updateSections = newOrder.map(section => {
        return {
          ...section,
          questions: section.questions.map( (question, index) => {
            return {
              ...question,
              questionId: `question_group_${formState.pk}_${formState.sk}_${section.sk || section.order}_${index}`
            }
          })
        }
      })
      setFormSections(updateSections);

      setSelectedSectionIndex(sectionIndex);

      setSelectedQuestionIndex(questionIndex);
  };

  const isGlobalFormReadOnlyForLocalAdmin =
    isLocalAdmin && formState.pk === Constants.FORM_GLOBAL;

  const blockLocalAdminGlobalSave = () => {
    setMessage('Local admins can view global forms but cannot save changes from the Edit tab.');
    setShowError(true);
  };

  const handleTranslationSave = async (updatedForm: FbeForm) => {
    // Version guard
    if (formResponse?.pk && formResponse?.sk && loadedVersionRef.current !== undefined) {
      try {
        const token = await getTokenFunction();
        const latest = await fbGetForm(formResponse.pk || '', formResponse.sk || '', token);
        if (latest && (latest.version ?? 0) > loadedVersionRef.current) {
          // Persist the draft to sessionStorage so it survives the reload
          try {
            const storageKey = `fbb-draft-translations-${formResponse.pk}-${formResponse.sk}`;
            sessionStorage.setItem(storageKey, JSON.stringify({
              name: updatedForm.name,
              descriptions: updatedForm.descriptions,
            }));
          } catch { /* ignore storage errors */ }
          setSaveConflict({ modifiedByName: latest.modifiedByName || latest.modifiedBy });
          return;
        }
      } catch { /* proceed optimistically */ }
    }
    const accessToken = await getTokenFunction();
    try {
      updateIsLoading(true);
      // Ensure pk/sk are present in the payload (fallback to known values)
      const payload = { ...updatedForm } as FbeForm;
      if ((!payload.pk || !payload.sk) && formResponse) {
        payload.pk = payload.pk || formResponse.pk;
        payload.sk = payload.sk || formResponse.sk;
      }
      if ((!payload.pk || !payload.sk) && originalFormState) {
        payload.pk = payload.pk || originalFormState.pk;
        payload.sk = payload.sk || originalFormState.sk;
      }

      const response = await fbPutForm(payload, accessToken);
      if (response) {
        const savedForm = JSON.parse(JSON.stringify(response)) as FbeForm & { workflowTaskType?: string; taskType?: string };
        // Preserve fields the server may not echo back
        if (!savedForm.description && payload.description) {
          savedForm.description = payload.description;
        }
        if ((!savedForm.descriptions || savedForm.descriptions.length === 0) && payload.descriptions && payload.descriptions.length > 0) {
          savedForm.descriptions = payload.descriptions;
        }
        if ((!savedForm.name || savedForm.name.length === 0) && payload.name && payload.name.length > 0) {
          savedForm.name = payload.name;
        }
        savedForm.workflowTask =
          savedForm.workflowTask ?? savedForm.workflowTaskType ?? savedForm.taskType ?? payload.workflowTask;
        delete savedForm.workflowTaskType;
        delete savedForm.taskType;
        setFormResponse(savedForm);
        setOriginalFormState(JSON.parse(JSON.stringify(savedForm)));
        setFormState(JSON.parse(JSON.stringify(savedForm)));
        loadedVersionRef.current = response.version;
        conflictBannerRef.current = null;
        setConflictBanner(null);
        setRestoredDraft(null);
        setTranslationsDirty(false);
        // Clear draft so Preview tab uses the freshly-saved formResponse, not stale draft
        setTranslationDraft({});
        setMessage('Translations saved!');
        setShowSuccess(true);
      } else {
        setShowError(true);
        setMessage('Unable to save translations!');
      }
    } catch (e) {
      setMessage(`Save translations error: ${e}`);
      setShowError(true);
    } finally {
      updateIsLoading(false);
    }
  };

  const handleEditChanges = async () => {
    if (isGlobalFormReadOnlyForLocalAdmin) {
      blockLocalAdminGlobalSave();
      return;
    }
    const accessToken = await getTokenFunction();
    let loadingSections = false;
    if (formState.workflowStatus === ('none' as unknown))
      formState.workflowStatus = FbeWorkflowStatus.NONE;
    try {
      updateIsLoading(true);
      const response = await fbPutForm(formState, accessToken);
      if (response) {
        const savedForm = JSON.parse(JSON.stringify(response)) as FbeForm & { workflowTaskType?: string; taskType?: string };
        // Preserve fields the server may not echo back
        if (!savedForm.description && formState.description) {
          savedForm.description = formState.description;
        }
        if ((!savedForm.descriptions || savedForm.descriptions.length === 0) && formState.descriptions && formState.descriptions.length > 0) {
          savedForm.descriptions = formState.descriptions;
        }
        savedForm.workflowTask =
          savedForm.workflowTask ?? savedForm.workflowTaskType ?? savedForm.taskType ?? formState.workflowTask;
        delete savedForm.workflowTaskType;
        delete savedForm.taskType;
        setFormResponse(savedForm);
        setOriginalFormState(JSON.parse(JSON.stringify(savedForm)));
        setFormState(JSON.parse(JSON.stringify(savedForm)));
        loadedVersionRef.current = response.version;
        conflictBannerRef.current = null;
        setConflictBanner(null);
        setMessage(fbPutFormSuccessMessage);
        setShowSuccess(true);
      } else {
        setShowError(true);
        setMessage(fbPutFormErrorMessage)
      }

      if(formSections && formSections.length > 0){
        loadingSections = true;
        const response = await fbPutSections(formState, formSections, accessToken);
        if (response) {
          setOriginalFormSections(JSON.parse(JSON.stringify(response)));
          setFormSections(JSON.parse(JSON.stringify(response)));
          setMessage(fbPutSectionsSuccessMessage);
          setShowSuccess(true);
        } else {
          setMessage(fbPutSectionsErrorMessage);
          setShowError(true);
        }
      }

    } catch (e) {
      setMessage(loadingSections ? `fbPutSections ${e}` : `fbPutForm ${e}`)
      setShowError(true);
    } finally {
      updateIsLoading(false);
    }

  };

  const handlePostChanges = async () => {
    if (isGlobalFormReadOnlyForLocalAdmin) {
      blockLocalAdminGlobalSave();
      return;
    }
    const accessToken = await getTokenFunction();
    if (formState.workflowStatus === ('none' as unknown))
      formState.workflowStatus = FbeWorkflowStatus.NONE;
    try {
      updateIsLoading(true);
      const postResponse = await fbPostForm(formState, accessToken) as FbeForm;
      if (postResponse) {
        setIsSaved(true);
        setFormResponse(JSON.parse(JSON.stringify(postResponse)));
        setOriginalFormState(JSON.parse(JSON.stringify(postResponse)));
        setMessage(fbPostFormSuccessMessage);
        setShowSuccess(true);
      } else {
        setShowError(true);
        setMessage(fbPostFormErrorMessage)
    }
    } catch (e) {
      setMessage(`fbPostForm ${e}`)
      setShowError(true);
    } finally {
      updateIsLoading(false);
    }
  };

  const handleAddCriteriaToList = (index: number) => {
    setFormState((prev) => {
      const updatedCriteria = {...(prev.criteria || {} ) as FbeCriteria} ;

      const newCriteria: FbeCharacteristics = {
        characteristicName: '',
        characteristicValue: '',
      };

      switch(index){
        case CharacteristicType.CRITERIA_CHARACTERISTICS_AND:
          if(!updatedCriteria.characteristics){
            updatedCriteria.characteristics = {};
          };
          if(!updatedCriteria.characteristics.and){
            updatedCriteria.characteristics.and = [];
          };
          updatedCriteria.characteristics.and.push(newCriteria)
          break;
        case CharacteristicType.CRITERIA_CHARACTERISTICS_OR:
          if(!updatedCriteria.characteristics){
            updatedCriteria.characteristics = {};
          };
          if(!updatedCriteria.characteristics.or){
            updatedCriteria.characteristics.or = [];
          };
          updatedCriteria.characteristics.or.push(newCriteria)
          break;
        case CharacteristicType.CRITERIA_NETWORK_AND:
          if(!updatedCriteria.networkCharacteristics){
            updatedCriteria.networkCharacteristics = {};
          };
          if(!updatedCriteria.networkCharacteristics.and){
            updatedCriteria.networkCharacteristics.and = [];
          };
          updatedCriteria.networkCharacteristics.and.push(newCriteria)
          break;
        case CharacteristicType.CRITERIA_NETWORK_OR:
          if(!updatedCriteria.networkCharacteristics){
            updatedCriteria.networkCharacteristics = {};
          };
          if(!updatedCriteria.networkCharacteristics.or){
            updatedCriteria.networkCharacteristics.or = [];
          };
          updatedCriteria.networkCharacteristics.or.push(newCriteria)
          break;
        default:
          break;
      }

      return { ...prev, criteria: updatedCriteria };
    });
  }

  const handleAddMapping = (externalIndex: number) => {
    const updatedExternalData = formState.externalData || [];

    const newMapping: ExternalDataMapping = {
      source: '',
      dest: '',
      type: '' as MappingType,
    };

    if(!updatedExternalData[externalIndex]){
      updatedExternalData[externalIndex] = {
        mapping: []
      };
    }

    updatedExternalData[externalIndex].mapping.push(newMapping);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {externalData, ...prev } = formState;

    setFormState({ ...prev, externalData: updatedExternalData });
  };

  const handleSubmitAllSections = async (sectionsOverride?: FbeSection[]) => {
    // Version guard — check for concurrent edits before saving
    if (formResponse?.pk && formResponse?.sk && loadedVersionRef.current !== undefined) {
      try {
        const token = await getTokenFunction();
        const latest = await fbGetForm(formResponse.pk || '', formResponse.sk || '', token);
        if (latest && (latest.version ?? 0) > loadedVersionRef.current) {
          setSaveConflict({ modifiedByName: latest.modifiedByName || latest.modifiedBy });
          return;
        }
      } catch { /* proceed optimistically on poll failure */ }
    }
    const accessToken = await getTokenFunction();
    try {
      updateIsLoading(true);
      if (formResponse) {
        const sections = sectionsOverride || formSections || [];
        const response = await fbPutSections(
          formResponse,
          sections,
          accessToken
        );
        if (response) {
          setOriginalFormSections(JSON.parse(JSON.stringify(response)));
          setFormSections(JSON.parse(JSON.stringify(response)));
          setMessage(fbPutSectionsSuccessMessage);
          setShowSuccess(true);
        } else {
          setShowError(true);
          setMessage(fbPutSectionsErrorMessage)
        }
      }
    } catch (e) {
      setMessage(`fbPutSections ${e}`)
      setShowError(true);
    } finally {
      updateIsLoading(false);
    }
  }

  const handleRemoveMapping = (
    externalIndex: number,
    mappingIndex: number
  ) => {
    const updatedExternalData = [...(formState.externalData || [])];

    // Remove the specific mapping at the provided index
    updatedExternalData[externalIndex].mapping = updatedExternalData[
      externalIndex
    ].mapping.filter((_, i) => i !== mappingIndex);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { externalData, ...prev } = formState;

    setFormState({ ...prev, externalData: updatedExternalData });
  };

  const handleRemoveCriteriaListData = (externalIndex: number, elementIndex: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { criteria, ...prev } = formState;

    const updatedCriteria = formState.criteria || {};

    switch(externalIndex){
      case 0:
        // Characteristics AND
        if(!updatedCriteria.characteristics){
          updatedCriteria.characteristics = {};
        };
        if(!updatedCriteria.characteristics.and){
          updatedCriteria.characteristics.and = [];
        };
        if(updatedCriteria.characteristics.and.length > 1){
          updatedCriteria.characteristics.and.splice(elementIndex,1);
        } else {
          delete updatedCriteria.characteristics.and;
        }
        break;
      case 1:
        // Characteristics OR
        if(!updatedCriteria.characteristics){
          updatedCriteria.characteristics = {};
        };
        if(!updatedCriteria.characteristics.or){
          updatedCriteria.characteristics.or = [];
        };
        if(updatedCriteria.characteristics.or.length > 1){
          updatedCriteria.characteristics.or.splice(elementIndex,1);
        } else {
          delete updatedCriteria.characteristics.or;
        }
        break;
      case 2:
        // Network AND
        if(!updatedCriteria.networkCharacteristics){
          updatedCriteria.networkCharacteristics = {};
        };
        if(!updatedCriteria.networkCharacteristics.and){
          updatedCriteria.networkCharacteristics.and = [];
        };
        if(updatedCriteria.networkCharacteristics.and.length > 1){
          updatedCriteria.networkCharacteristics.and.splice(elementIndex,1);
        } else {
          delete updatedCriteria.networkCharacteristics.and;
        }
        break;
      case 3:
        // Network OR
        if(!updatedCriteria.networkCharacteristics){
          updatedCriteria.networkCharacteristics = {};
        };
        if(!updatedCriteria.networkCharacteristics.or){
          updatedCriteria.networkCharacteristics.or = [];
        };
        if(updatedCriteria.networkCharacteristics.or.length > 1){
          updatedCriteria.networkCharacteristics.or.splice(elementIndex,1);
        } else {
          delete updatedCriteria.networkCharacteristics.or;
        }
        break;
      default:
        break;
    }
      if(updatedCriteria.characteristics && Object.keys(updatedCriteria.characteristics).length === 0){
        delete updatedCriteria.characteristics;
      }
      if(updatedCriteria.networkCharacteristics && Object.keys(updatedCriteria.networkCharacteristics).length === 0){
        delete updatedCriteria.networkCharacteristics;
      }

      setFormState({ ...prev, criteria: updatedCriteria });
  }

  const getExternalDataType = (
    externalIndex: number,
    mappingIndex: number) : string => {
      if(formState.externalData && formState.externalData.length > 0){
        if(formState.externalData[externalIndex].mapping && (mappingIndex < formState.externalData[externalIndex].mapping.length)){
          return formState.externalData[externalIndex].mapping[mappingIndex].type;
        }
      }

      return MappingType.BOOLEAN;
  }

  const getExternalDataOperator = (
    externalIndex: number,
    mappingIndex: number) : string => {
      if(formState.externalData && formState.externalData.length > 0){
        if(formState.externalData[externalIndex].mapping && (mappingIndex < formState.externalData[externalIndex].mapping.length)){
          return formState.externalData[externalIndex].mapping[mappingIndex].operator || '';
        }
      }

      return '';
  }

  const criteriaInfoList = [
    {
      index: 0,
      title: 'SAP characteristics (all from a list)',
      list: formState.criteria?.characteristics?.and || []
    },
    {
      index: 1,
      title: 'SAP characteristics (one from a list)',
      list: formState.criteria?.characteristics?.or || []
    },
    {
      index: 2,
      title: 'SAP network fields (all from a list)',
      list: formState.criteria?.networkCharacteristics?.and || []
    },
    {
      index: 3,
      title: 'SAP network fields (one from a list)',
      list: formState.criteria?.networkCharacteristics?.or || []
    }
  ];

  const externalDataInfoList = [
    {
      index: 0,
      title: 'SAP characteristics mapping(s)',
      list: formState.externalData && formState.externalData.length > 0 ? formState.externalData[0] : []
    },
    {
      index: 2,
      title: 'SAP network fields mapping(s)',
      list: formState.externalData && formState.externalData.length > 1 ? formState.externalData[1] : []
    }
  ];

  const generateCountryOptions = (
    frontline: FbeFrontline | null,
    appliedCountries: string[],
  ): FbeOption[] => {
    if(!frontline){
        return []
    }

    const countries = (frontline.settings[frontline.frontline] as FbeFrontlineInfo).countries;

    const list = countries.map( country => {
      return {
        value: country.toLowerCase(),
        label: getCountryDisplayName(country.toLowerCase(), frontline.settings.countryDisplayNames),
        key: 'frontlineCountries',
        disabled: false,
      }
    });

    if(appliedCountries.length > 0  && appliedCountries[0].toLowerCase() === 'global' && !list.some(o => o.value === 'global')){
      list.push({
        value: 'global',
        label: getCountryDisplayName('global', frontline.settings.countryDisplayNames),
        key: 'frontlineCountries',
        disabled: false,
      });
    }

    return list;
  }

  const isEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const generateReceiverOptions = (options: string[]) : FbeOption[] => {
    const optionList: FbeOption[] = [...receiversOptions, ...roleOptions];

    options.map(option => {
      const role = roleOptions.find(role => role.value === option);
      const receiver = receiversOptions.find(role => role.value === option);

      if(!role && !receiver && isEmail(option)){
        optionList.push({
          value: option,
          label: option,
          key: 'role',
          disabled: false,
        })
      }
    });

    return optionList;
  }

  const giNameHelpTitle='Form name (mandatory)';
  const giNameHelpBody='Give an easy, short readable name to the form that encompasses the essence of it.';
  const giActiveTitle='Active (mandatory)';
  const giActiveBody='When checked the form is used when applicable. <><>Leave it unchecked if you don\'t want it to be used yet or keep it as archive.';
  const giModeTitle='Mode (mandatory)';
  const giModeBody='Select a test mode to test the form (after release). <><>Select Installer (qdplus) to test the form in the installer flow.<><>Select Tester (nebseb) to test the form in the tester flow.';
  const giDescriptionHelpTitle='Description (optional)';
  const giDescriptionHelpBody='Add a description to explain what the form is about and and important information such that other administrators understand the purpose of this form.';
  const giOrderHelpTitle='Order (mandatory)';
  const giOrderHelpBody='Use the order field to indicate the priority of a form. <><>In the case in which multiple forms can be applied the order matters. <><>Forms with lower numbers are handled before forms with higher numbers.';
  const giFormIDHelpTitle='Form ID (mandatory)';
  const giFormIDHelpBody='Use a short id to make the form unique or use an existing form ID in case of form extension to connect this form to the original form.<><>Preferable format is lower case, no spaces and optional underscores.';

  const dAppliedCountriesTitle = 'Applied countries (mandatory)';
  const dAppliedCountriesBody = 'Select the countries to which this form applies.<><>Select at least one country.'

  const dRolesTitle = 'Role(s) (mandatory)';
  const dRolesBody = 'Choose the role(s) who should fill in this form.';
  const dActiveWorkflowTitle = 'Active workflow state(s) (mandatory)';
  const dActiveWorkflowBody = 'Select the workflow states in which the form needs to be active.';

  const dFeatureTitle = 'Feature(s) (optional)';
  const dFeatureBody = 'Select the features(s) for the form (when supported by displaying application).<><>Allow role changes by supervisor to allow the supervisor to alter the roles for this form.<><>Allow additions by supervisor to allow the supervisor to add questions to this form.<><>Allow removals by supervisor to allow the supervisor to remove questions from this form.<><>No local sections allowed (extend) to indicate no local sections can be added when extending this global form. <><>Mandatory to indicate this form must be filled by the user.<><>Allow to skip questions to allow users to continue even when not all questions are filled. <><>Display summary to display a summary page at the end of the form.<><>No open deviations prohibits handover when open deviations exists.<><>Signoff to add a signof section to the form.<><>Site information add general site information in front of the form.';
  const dGenerateTriggerTitle = 'Generate form trigger (mandatory)';
  const dGenerateTriggerBody = 'Select the workflow state when the form should be generated. When (IES) is mentioned it refers to the IES workflow.<><>To be started (IES) when the form needs to be gerenated at data ingestion flow. <><>For installer acceptance (IES) when the form needs to be generated when the supervisor releases the network.';
  const dFormTypeTitle = 'Form type (mandatory)';
  const dFormTypeBody = 'Form type defines how the form is generated in relation to existing forms. <><>Add if not exists = if a form with form ID exists then nothing is done, otherwise the form is added. <><>Independent = form is always added. <><>Full Merge = when extending a global form, this type is set and cannot be changed. <><>Merge (after) = if a form with the form ID exists then the form is added to the existing form, otherwise it behaves as independent. <><>Replace = if a form with the form ID exists then the old form is deleted, then the new form is added.';
  const dRepeatTypeTitle = 'Repeat type (mandatory)';
  const dRepeatTypeBody = 'Defined how many times the form should be used in the displaying application.<><>One time if the form needs to be filled in only one time (per role). <><>Recurring daily is used when the form needs a to be filled daily depending on the recurring criteria. <><>Recurring weekly is used when the form needs a to be filled weekly depending on the recurring criteria. ';

  const dQuestionSetIDRangeHelpTitle = 'Question Set ID range (automatic)';
  const dQuestionSetIDRangeHelpBody = 'This value is set after the first save action. It provides info for the sections.';

  const sarUseScoringTitle = 'Form scoring (optional)';
  const sarUseScoringBody = 'Enable form scoring by checking the box. <><>When leaving empty no scoring will be applied.';
  const sarPercentPassTitle = 'Pass percentage  (optional/ mandatory)';
  const sarPercentPassBody = 'Enter a value between 0 and 100 to indicate which percentage of the score is enough to pass.<><> Calculating a form score and determining a pass or fail is dependent on the total scoring questions, the weight of each question and must score indication.';
  const sarUseDatabaseTitle = 'Use database  (optional)';
  const sarUseDatabaseBody = 'Enable form answer storing in the database by checking the box.';
  const sarGenerateResultTitle = 'Generate result  (mandatory)';
  const sarGenerateResultBody = 'Select the state when the result of the form should be processed. When (IES) is mentioned it refers to the IES workflow.<><>None indicates no automatic trigger.<><>Ready for test inspection (IES) indicates when a network becomes available for testing.<><> After test inspection (IES) when the tests have completed.<><>Ready for SEB inspection (IES) indicates when a network becomes available for SEB inspection.<><>Accepted by SEB (IES) when SEB accepts the network.<><>Rejected by SEB (IES) when SEB rejects the network.<><>Installation complete (IES) when the network is ready.';
  const sarResultReceiverTitle = 'Receivers (mandatory)';
  const sarResultReceiverBody = 'Add a list of roles to which the form result is send by email. <><>Select None to disable sending emails. <><>When selecting one or more roles the email addresses in workday are used as destinations.';

  const fdSourceTitle = 'Source (mandatory)';
  const fdSourceBody = 'Enter the name of the characteristic key or field name in the SAP network record. ';
  const fdValueTitle = 'Value (mandatory)';
  const fdValueBody = 'Enter the expected value.';
  const fdAddTitle = 'Add new criteria';
  const fdAddBody = 'Add a new source/ value pair of input values. <><>Add them at the right section.';

  const edSourceTitle = 'Source (mandatory)';
  const edSourceBody = 'Enter a SAP characteristic name from which the network value is used as variable. ';
  const edDestinationTitle = 'Destination (mandatory)';
  const edDestinationBody = 'Enter a variable name to be used in the section(s) and/ or question(s). <><>Preferable format is to prefix with a $, then lower case, no spaces and optional underscores.';
  const edTypeTitle = 'Type (mandatory)';
  const edTypeBody = 'Select the type of the value to be used.<><>Boolean is used for true or false.<><>Number is used for all number formats. <><>String is used for all other types.';
  const edOperatorTitle = 'Operator (optional)';
  const edOperatorBody = 'Select an operator if you need the variable to contain the result of that operator. Set the type to Boolean in that case.<><>Equals when the source needs to be the same as the compare value.<><>Includes when the source needs to be one of the values in compare value.<><>NotEquals when the source needs to be different from the compare value.';
  const edCompareTitle = 'Compare (optional)';
  const edCompareBody = 'When selecting an operator one needs to provide the value to compare against. <><>When using Includes provide multiple values separated by - (minus) sign. ';
  const edDefaultTitle = 'Default (optional)';
  const edDefaultBody = 'Add a static value to the variable or provide the default value when the source does not exists. ';
  const edAddMapTitle = 'Add mapping (optional)';
  const edAddMapBody = 'Add a new mapping definition to the form. ';
  const edRemoveMapTitle = 'Remove mapping (optional)';
  const edRemoveMapBody = 'Remove above mapping definition from the form. ';
  const edRemoveCriteriaTitle = 'Remove criteria (optional)';
  const edRemoveCriteriaBody = 'Remove above criteria definition from the form. ';

  const fdAddResultHandlingTitle = 'Add new result handling (optional)';
  const fdAddResultHandlingBody = 'Add a new result handling at a different state';
  const edRemoveResultHandlingTitle = 'Remove result handling (optional)';
  const edRemoveResultHandlingBody = 'remove one of the additional result handlings.';

  const isInitial = false;

  const validGeneralInfo = isInitial || isFormValid(formState,['name','active','role','appliedCountries','order','formId','defaultLanguage','workflowTask']);
  const validDetails = isInitial || isFormValid(formState,['activeWorkflowStatus','workflowStatus','repeatType','type']);
  const validResults = isInitial || isFormValid(formState,['resultWorkflowStatus','resultEmailReceivers']);
  const validScoring = true;
  const validDependency = isInitial || isFormValid(formState,['criteria']);
  const validExternalData = isInitial || isFormValid(formState,['externalData']);

  const changedGeneralInfo = hasFormChanges(originalFormState,formState,(['name','active','mode','role','appliedCountries','order','formId','description','defaultLanguage','workflowTask'] as (keyof FbeForm)[]));
  const changedDetails = hasFormChanges(originalFormState,formState,['activeWorkflowStatus','features','workflowStatus','repeatType','type','questionSetIdRange'] as (keyof FbeForm)[]);
  const changedResults = hasFormChanges(originalFormState,formState,['resultHandling'] as (keyof FbeForm)[]);
  const changedScoring = hasFormChanges(originalFormState,formState,['passingScorePercentage'] as (keyof FbeForm)[]);
  const changedDependency = hasFormChanges(originalFormState,formState,['criteria'] as (keyof FbeForm)[]);
  const changedExternalData = hasFormChanges(originalFormState,formState,['externalData'] as (keyof FbeForm)[]);

  const currentLanguages: string[] = [];
  if(formState && formState.name && formState.name.length > 0){
    formState.name.map(element => {currentLanguages.push(element.language)})
  }

  const baseLanguage = currentLanguages.length > 0 ? currentLanguages[0] : 'invalid';

  const locksDisabled = !formState || (
    formState.pk !== 'form_global' && formState.type === FbeType.FULL_MERGE);

  const locksDisplayed = formState && (
    formState.pk === 'form_global' || formState.type === FbeType.FULL_MERGE);

  const legacyFormState = formState as FbeForm & { workflowTaskType?: string; taskType?: string };
  const workflowTaskValue = formState.workflowTask ?? legacyFormState.workflowTaskType ?? legacyFormState.taskType;
  const selectedWorkflowTaskOption = formWorkflowTaskOptions.find((option) => option.value === workflowTaskValue);

    return (
    <>
        <LoadingSpinner />
        <Divider sx={{ backgroundColor: '#707070' }} />
          <Container
            sx={{
              paddingLeft: 0,
              paddingRight: 0,
              maxWidth: 'none !important',
            }}
          >

            {/* Last modified by — always visible */}
            {formState.modifiedByName && formState.modifiedAt && (
              <Typography variant="caption" sx={{ color: '#000', backgroundColor: '#e3f2fd', display: 'block', textAlign: 'center', padding: '4px 12px' }}>
                Last edited by <strong>{formState.modifiedByName}</strong> &middot; {new Date(formState.modifiedAt).toLocaleString()}
              </Typography>
            )}

            <Grid container>
              {/* Sidebar */}
                <Sidebar
                readOnly={readOnlyForm || readOnly}
                language={language}
                isLocalAdmin={isLocalAdmin}
                key={updateSidebarCode}
                selectedIndex={selectedIndex}
                selectedSectionIndex={selectedSectionIndex}
                selectedQuestionIndex={selectedQuestionIndex}
                handleListItemClick={handleListItemClick}
                handleSectionListItemClick={handleSectionListItemClick}
                handleQuestionListItemClick={handleQuestionListItemClick}
                formName={getLocalizedText(formState.name || [], formState.defaultLanguage || formState.name?.[0]?.language || 'en') || ''}
                form={formState}
                originalForm={originalFormState}
                sections={formSections || []}
                originalSections={originalFormSections || []}
                onAddNewSection={handleAddNewSection}
                onAddNewQuestion={handleAddNewQuestion}
                onDeleteSection={handleDeleteSection}
                onReorderSections={handleReorderSections}
                onDeleteQuestion={handleDeleteQuestion}
                onReorderQuestions={handleReorderQuestions}
                onDuplicateSection={handleDuplicateSection}
                onDuplicateQuestion={handleDuplicateQuestion}
              />

              {/* Main Content */}
              {selectedIndex === 0 &&
                (selectedSectionIndex === null ||
                  selectedSectionIndex === -1) &&
                (selectedQuestionIndex === null ||
                  selectedSectionIndex === -1) && (
                  <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                  <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ backgroundColor: '#f5f5f5', padding: '8px' }}
                    >
                      <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        aria-label="form tabs"
                      >
                        <Tab
                          label={
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <EditOutlined sx={{ marginRight: '8px' }} />
                              Edit
                            </div>
                          }
                        />
                        <Tab
                          label={
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <TranslateOutlined sx={{ marginRight: '8px' }} />
                              Translations
                            </div>
                          }
                        />
                        <Tab
                          label={
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <VisibilityOutlined sx={{ marginRight: '8px' }} />
                              Preview
                            </div>
                          }
                        />
                      </Tabs>

                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ marginRight: '8px' }}>
                          Localization:
                        </Typography>
                        <Select
                          id="form-language-select"
                          name="language"
                          value={supportedLanguages.includes(language) ? language : (supportedLanguages[0] || '')}
                          onChange={handleLanguageChange}
                          sx={{ minWidth: 120 }}
                          inputProps={{
                            'aria-label': 'Form language selection'
                          }}
                        >
                        {
                        supportedLanguages.length > 0 ? supportedLanguages.map(languageOption => (
                            <MenuItem value={languageOption} selected={language === languageOption} data-testid={`form-language-${languageOption}`}>
                            {getLanguageDisplayName(languageOption, frontline?.settings.languageDisplayNames || {})} { currentLanguages.includes(languageOption) ? (baseLanguage === languageOption ? '(*)' : '(T)') : ''}
                            </MenuItem>
                        )) : (<></>)}
                        </Select>
                      </Box>
                    </Box>

                    <Box
                      mt={3}
                      sx={{ paddingLeft: '24px', paddingRight: '24px' }}
                    >
                      {tabIndex === 0 && (
                        <>
                          <Box>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="h6">
                                 <FieldLock
                                  onChange={function (locked: boolean): void {
                                      handleAllLockedFields(locked);
                                  } }
                                  labelId={'all'}
                                  size={24}
                                  disabled={locksDisabled}
                                  displayed={locksDisplayed}
                                  value={(formState.lockedFields || []).length === fbeFormFields.length}/>
                                Edit Form
                              </Typography>
                            </Box>
                            {/* Concurrent-edit warning banner */}
                            {conflictBanner && (
                              <Alert
                                severity="warning"
                                sx={{ mt: 1, mb: 1 }}
                                action={
                                  <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                                    Reload
                                  </Button>
                                }
                              >
                                <strong>{conflictBanner.modifiedByName || 'Another user'}</strong> saved changes to this form while you were editing
                                {conflictBanner.modifiedAt ? ` (${new Date(conflictBanner.modifiedAt).toLocaleString()})` : ''}.
                                {' '}Please reload to get the latest version before saving, to avoid overwriting newer changes.
                              </Alert>
                            )}
                            <Box mt={2}>

                              <InputField
                                labelText={'Name'}
                                labelId={'name'}
                                mandatory={true}
                                helpTextTitle={giNameHelpTitle}
                                helpTextBody={giNameHelpBody}
                                onChange={(value) =>
                                  handleChange('name', value,language)
                                }
                                onBlur={() =>
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    name: true,
                                  }))
                                }
                                fullWidth={true}
                                value={formState.name?.[0]?.text || ''}
                                error={
                                  touchedFields.name && !formState.name[0].text
                                }
                                helperText={
                                  touchedFields.name && !formState.name[0].text
                                    ? 'Name is required'
                                    : ''
                                }

                                disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('name') )}
                                onLock={handleLockedFields}
                                disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                isLocked={(formState.lockedFields || []).includes('name')}
                              />
                              <FBCheckbox
                                checked={formState.active}
                                labelText={'Active'}
                                labelId={'active'}
                                mandatory={true}
                                helpTextTitle={giActiveTitle}
                                helpTextBody={giActiveBody}
                                onChange={(checked) =>
                                  handleChange('active', checked)
                                }
                                disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('active') )}
                                onLock={handleLockedFields}
                                disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                isLocked={(formState.lockedFields || []).includes('active')}
                              />
                            </Box>

                            <SmallTabBoxStyleStyled>
                              <Tabs
                                value={smallTabIndex}
                                onChange={handleSmallTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ flexGrow: 1, minWidth: 0 }}
                              >
                                <Tab
                                  label="General Info"
                                  sx={{
                                    ...( changedGeneralInfo ? { backgroundColor: '#F9E20D' } : {} ),
                                    ...( !validGeneralInfo ? { backgroundColor: '#FF2A31' } : {} ),
                                    margin:'4px',
                                    minWidth: 'auto',
                                    width: 'auto',
                                    flex: 1,
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                  }}
                                />
                                <SmallTabStyleStyled
                                  label="Details"
                                  sx={{
                                    ...( changedDetails ? { backgroundColor: '#F9E20D' } : {} ),
                                    ...( !validDetails ? { backgroundColor: '#FF2A31' } : {} ),
                                    margin:'4px',
                                  }}
                                />
                                <SmallTabStyleStyled
                                  label="Results"
                                  sx={{
                                    ...( changedResults ? { backgroundColor: '#F9E20D' } : {} ),
                                    ...( !validResults ? { backgroundColor: '#FF2A31' } : {} ),
                                    margin:'4px',
                                  }}
                                />
                                <SmallTabStyleStyled
                                  label="Scoring"
                                  sx={{
                                    ...( changedScoring ? { backgroundColor: '#F9E20D' } : {} ),
                                    ...( !validScoring ? { backgroundColor: '#FF2A31' } : {} ),
                                    margin:'4px',
                                  }}
                                />
                                <SmallTabStyleStyled
                                  label="Form Dependency"
                                  sx={{
                                    ...( changedDependency ? { backgroundColor: '#F9E20D' } : {} ),
                                    ...( !validDependency ? { backgroundColor: '#FF2A31' } : {} ),
                                    margin:'4px',
                                  }}
                                />
                                <SmallTabStyleStyled
                                  label="External Data"
                                  sx={{
                                    ...( changedExternalData ? { backgroundColor: '#F9E20D' } : {} ),
                                    ...( !validExternalData ? { backgroundColor: '#FF2A31' } : {} ),
                                    margin:'4px',
                                  }}
                                />
                              </Tabs>
                            </SmallTabBoxStyleStyled>
                            <EditContainer>
                              {smallTabIndex === 0 && (
                                <Box>
                                  {/* Roles */}
                                  <InputRowContainer>
                                  <MultiSelect
                                    labelText={'Role(s)'}
                                    labelId={'role'}
                                    mandatory={true}
                                    fullWidth={true}
                                    helpTextTitle={dRolesTitle}
                                    helpTextBody={dRolesBody}
                                    onChange={(value) => {
                                      handleChange('role', value);
                                      setTouchedFields((prev) => ({
                                        ...prev,
                                        roles: true,
                                      })); // Mark as touched
                                    }}
                                    options={roleOptions}
                                    selectedValues={roleOptions.filter(option => `${formState.role}`.includes(option.value))}
                                    disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('role') )}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                                    displayedLock={locksDisplayed}
                                    isLocked={(formState.lockedFields || []).includes('role')}
                                  />
                                  </InputRowContainer>
                                  <InputRowContainer>
                                  <MultiSelect
                                    labelText={'Applied countries'}
                                    labelId={'appliedCountries'}
                                    mandatory={true}
                                    fullWidth={true}
                                    helpTextTitle={dAppliedCountriesTitle}
                                    helpTextBody={dAppliedCountriesBody}
                                    onChange={(value) => {
                                      handleChange('appliedCountries', value);
                                      setTouchedFields((prev) => ({
                                        ...prev,
                                        appliedCountries: true,
                                      })); // Mark as touched
                                    }}
                                    options={generateCountryOptions(frontline, formState.appliedCountries)}
                                    selectedValues={generateCountryOptions(frontline, formState.appliedCountries).filter(option => formState.appliedCountries.includes(option.value))}
                                    disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('appliedCountries') )}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                    isLocked={(formState.lockedFields || []).includes('appliedCountries')}
                                  />
                                  </InputRowContainer >
                                  <InputRowContainer>
                                  <SingleSelect
                                    labelText={'Default language'}
                                    labelId={'defaultLanguage'}
                                    mandatory={true}
                                    fullWidth={true}
                                    helpTextTitle={'Default language'}
                                    helpTextBody={'The source/base language that form content is authored in. Used as the reference language for translation completion percentages.'}
                                    onChange={(value) => handleChange('defaultLanguage', value)}
                                    options={supportedLanguages.map((lang) => ({
                                      value: lang,
                                      label: getLanguageDisplayName(lang, frontline?.settings.languageDisplayNames || {}),
                                      key: 'defaultLanguage',
                                      disabled: false,
                                    }))}
                                    selectedValues={formState.defaultLanguage
                                      ? [{
                                          value: formState.defaultLanguage,
                                          label: getLanguageDisplayName(formState.defaultLanguage, frontline?.settings.languageDisplayNames || {}),
                                          key: 'defaultLanguage',
                                          disabled: false,
                                        }]
                                      : []}
                                    disabled={readOnlyForm}
                                    onLock={() => {}}
                                    disabledLock={true}
                                    displayedLock={false}
                                    isLocked={false}
                                  />
                                  <SingleSelect
                                    labelText={'Workflow Task'}
                                    labelId={'workflowTask'}
                                    mandatory={false}
                                    helpTextTitle={'Workflow Task (optional)'}
                                    helpTextBody={'Onsite workflow task/subtask'}
                                    onChange={(value) => handleChange('workflowTask', value)}
                                    options={formWorkflowTaskOptions}
                                    selectedValues={selectedWorkflowTaskOption ? [selectedWorkflowTaskOption] : []}
                                    disabled={readOnlyForm || (locksDisabled && ((formState.lockedFields || []).includes('workflowTask') || (formState.lockedFields || []).includes('workflowTaskType') || (formState.lockedFields || []).includes('taskType')) )}
                                    fullWidth={true}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                                    displayedLock={locksDisplayed}
                                    isLocked={(formState.lockedFields || []).includes('workflowTask') || (formState.lockedFields || []).includes('workflowTaskType') || (formState.lockedFields || []).includes('taskType')}
                                  />
                                  </InputRowContainer>

                                  {/* Description */}
                                  <InputRowContainer>

                              {/* <InputField
                                labelText={'Description'}
                                labelId={'description'}
                                mandatory={false}
                                helpTextTitle={giDescriptionHelpTitle}
                                helpTextBody={giDescriptionHelpBody}
                                onChange={(value) =>
                                  handleChange('description', value)
                                }
                                onBlur={() =>
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    description: true,
                                  }))
                                }
                                fullWidth={true}
                                rows={4}
                                value={formState.description || ''}
                                error={false}
                                helperText={''}

                                disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('description') )}
                                onLock={handleLockedFields}
                                disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                isLocked={(formState.lockedFields || []).includes('description')}
                              /> */}
                              <RichTextField
                              labelText={'Description'}
                              labelId={'description'}
                              mandatory={false}
                              helpTextTitle={giDescriptionHelpTitle}
                              helpTextBody={giDescriptionHelpBody}
                              onChange={(value) =>
                                handleChange('description', value)
                              }
                              onBlur={() =>
                                setTouchedFields((prev) => ({
                                  ...prev,
                                  description: true,
                                }))
                              }
                              value={formState.description || ''}
                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('description'))}
                              onLock={handleLockedFields}
                              displayedLock={locksDisplayed}
                              disabledLock={locksDisabled}
                              locked={formState.lockedFields?.includes('description')}
                              error={false}
                              helperText={''}
                              />
                              </InputRowContainer>
                              <InputRowContainer>
                              <Box sx={{display:'flex', flexDirection:'row', alignItems:'flex-start', gap:2, width:'100%'}}>
                                <Box sx={{display:'flex', flexDirection:'column', gap:0.5}}>
                                  <InputField
                                  labelText={'Order'}
                                  labelId={'order'}
                                  mandatory={true}
                                  helpTextTitle={giOrderHelpTitle}
                                  helpTextBody={giOrderHelpBody}
                                  onChange={(value) =>
                                    handleChange('order', Number(value))
                                  }
                                  onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      order: true,
                                    }))
                                  }
                                  fullWidth={false}
                                  value={`${formState.order}`}
                                  error={
                                    touchedFields.order &&
                                    formState.order < 0
                                  }
                                  helperText={
                                    touchedFields.order &&
                                    formState.order < 0
                                      ? 'Order is required'
                                      : ''
                                  }
                                  type="number"

                                  disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('order') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={locksDisplayed}
                                  isLocked={(formState.lockedFields || []).includes('order')}
                                />
                                  <Box sx={{display:'flex', alignItems:'center', gap:1}}>
                                    <IconButton
                                      size="small"
                                      onClick={handleOpenOrderList}
                                      disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('order') )}
                                      aria-label="existing-order-ids"
                                      sx={{ mt: 0.5 }}
                                    >
                                      <ListAlt fontSize="small" />
                                    </IconButton>
                                    <Typography
                                      onClick={(e: any) => {
                                        const disabled = readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('order'));
                                        if (!disabled) handleOpenOrderList(e);
                                      }}
                                      onKeyDown={(e: any) => {
                                        const disabled = readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('order'));
                                        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                                          e.preventDefault();
                                          handleOpenOrderList(e);
                                        }
                                      }}
                                      sx={{fontSize:12, color:'text.secondary', userSelect:'none', cursor: (readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('order'))) ? 'default' : 'pointer'}}
                                      role="button"
                                      tabIndex={0}
                                    >Existing Order IDs</Typography>
                                  </Box>
                                  <Popover
                                    anchorEl={orderAnchorEl}
                                    open={Boolean(orderAnchorEl)}
                                    onClose={handleCloseOrderList}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    PaperProps={{ sx: { p: 0, borderRadius: 2, boxShadow: 3 } }}
                                  >
                                    <List sx={{ minWidth: 260, maxHeight: 320, overflow: 'auto', userSelect: 'none', p:0 }}>
                                      {allFormsList && allFormsList.length > 0 ? (
                                        allFormsList.map((f, idx) => {
                                          const title = f.name && f.name.length > 0 ? f.name[0].text : (f.formId || f.pk || `form-${idx}`);
                                          const order = f.order !== undefined && f.order !== null ? String(f.order) : '—';
                                          return (
                                            <ListItem key={`${f.pk || idx}-${f.sk || idx}`} disablePadding sx={{ px: 1.5, py: 0.75 }}>
                                              <ListItemText primary={`${title} - ${order}`} primaryTypographyProps={{ fontSize: 14 }} sx={{ userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} />
                                            </ListItem>
                                          )
                                        })
                                      ) : (usedOrders && usedOrders.length > 0 ? (
                                        usedOrders.map((o) => (
                                          <ListItem key={o} disablePadding sx={{ px: 1.5, py: 0.75 }}>
                                            <ListItemText primary={`${o}`} primaryTypographyProps={{ fontSize: 14 }} sx={{ userSelect: 'none' }} />
                                          </ListItem>
                                        ))
                                      ) : (
                                        <ListItem sx={{ px: 1.5, py: 0.75 }}>
                                          <ListItemText primary="No used orders" primaryTypographyProps={{ fontSize: 14 }} sx={{ userSelect: 'none' }} />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Popover>
                                </Box>
                                <Box sx={{flex:1}}>
                                  <InputField
                                labelText={'Form ID'}
                                labelId={'formId'}
                                mandatory={true}
                                helpTextTitle={giFormIDHelpTitle}
                                helpTextBody={giFormIDHelpBody}
                                onChange={(value) =>
                                  handleChange('formId', value)
                                }
                                onBlur={() =>
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    formId: true,
                                  }))
                                }
                                fullWidth={true}
                                value={formState.formId}
                                error={
                                  touchedFields.formId &&
                                  formState.formId === ''
                                }
                                helperText={
                                  touchedFields.order &&
                                  formState.order < 0
                                    ? 'Form ID is required'
                                    : ''
                                }

                                disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('formId') )}
                                onLock={handleLockedFields}
                                disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                isLocked={(formState.lockedFields || []).includes('formId')}
                              />
                                </Box>
                              </Box>
                              </InputRowContainer>
                            </Box>
                          )}
                              {smallTabIndex === 1 && (
                                <Box>

                                  {/* Active workflow states */}
                                  <InputRowContainer>
                                  <MultiSelect
                                    labelText={'Active workflow state(s)'}
                                    labelId={'activeWorkflowStatus'}
                                    mandatory={true}
                                    fullWidth={true}
                                    helpTextTitle={dActiveWorkflowTitle}
                                    helpTextBody={dActiveWorkflowBody}
                                    onChange={(value) => {
                                      handleChange('activeWorkflowStatus', value);
                                      setTouchedFields((prev) => ({
                                        ...prev,
                                        roles: true,
                                      })); // Mark as touched
                                    }}
                                    options={activeWorkflowStatusOptions}
                                    selectedValues={activeWorkflowStatusOptions.filter(option => `${formState.activeWorkflowStatus}`.includes(option.value))}
                                    disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('activeWorkflowStatus') )}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                                    displayedLock={locksDisplayed}
                                    isLocked={(formState.lockedFields || []).includes('activeWorkflowStatus')}
                                    showWorkflowInfo={true}
                                  />

                                  </InputRowContainer>

                                  {/* Features */}
                                  <InputRowContainer>
                                  <MultiSelect
                                    labelText={'Feature(s)'}
                                    labelId={'features'}
                                    mandatory={false}
                                    fullWidth={true}
                                    helpTextTitle={dFeatureTitle}
                                    helpTextBody={dFeatureBody}
                                    onChange={(value) => {
                                      handleChange('features', value);
                                      setTouchedFields((prev) => ({
                                        ...prev,
                                        roles: true,
                                      })); // Mark as touched
                                    }}
                                    options={featureOptions}
                                    selectedValues={featureOptions.filter(option => `${formState.features}`.includes(option.value))}
                                    disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('features') )}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                    isLocked={(formState.lockedFields || []).includes('features')}
                                  />
                                  </InputRowContainer>

                                  {/* Trigger Before */}
                                  <InputRowContainer>
                                    <SingleSelect
                                      labelText={'Generate form trigger'}
                                      labelId={'workflowStatus'}
                                      mandatory={true}
                                      fullWidth={true}
                                      helpTextTitle={dGenerateTriggerTitle}
                                      helpTextBody={dGenerateTriggerBody}
                                      onChange={(value) => {
                                        handleChange(
                                          'workflowStatus',
                                          value
                                        )
                                      }}
                                      options={generateTriggerOptions}
                                      selectedValues={generateTriggerOptions.filter(option => formState.workflowStatus === (option.value as unknown as FbeWorkflowStatus))}
                                      disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('workflowStatus') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                      displayedLock={locksDisplayed}
                                      isLocked={(formState.lockedFields || []).includes('workflowStatus')}
                                      showWorkflowInfo={true}
                                    />

                                    <SingleSelect
                                      labelText={'Form type'}
                                      labelId={'formType'}
                                      mandatory={true}
                                      fullWidth={true}
                                      helpTextTitle={dFormTypeTitle}
                                      helpTextBody={dFormTypeBody}
                                      onChange={(value) => {
                                        handleChange(
                                          'type',
                                          value
                                        )
                                      }}
                                      options={formTypeOptions}
                                      selectedValues={formTypeOptions.filter(option => formState.type === (option.value as unknown as FbeType))}
                                      disabled={formState.type === FbeType.FULL_MERGE || readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('formType') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                      isLocked={(formState.lockedFields || []).includes('formType')}
                                    />
                                  </InputRowContainer>
                                  <InputRowContainer>

                                    <SingleSelect
                                      labelText={'Repeat type'}
                                      labelId={'repeatType'}
                                      mandatory={true}
                                      fullWidth={true}
                                      helpTextTitle={dRepeatTypeTitle}
                                      helpTextBody={dRepeatTypeBody}
                                      onChange={(value) => {
                                        handleChange(
                                          'repeatType',
                                          value
                                        )
                                      }}
                                      options={repeatTypeOptions}
                                      selectedValues={repeatTypeOptions.filter(option => formState.repeatType === (option.value as unknown as FbeRepeatType))}
                                      disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('repeatType') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                      isLocked={(formState.lockedFields || []).includes('repeatType')}
                                    />
                                      <SingleSelect
                                        labelText={'Mode'}
                                        labelId={'mode'}
                                        mandatory={true}
                                        fullWidth={true}
                                        helpTextTitle={giModeTitle}
                                        helpTextBody={giModeBody}
                                        onChange={(value) => {
                                          handleChange(
                                            'mode',
                                            value
                                          )
                                        }}
                                        options={modeOptions}
                                        selectedValues={modeOptions.filter(option => formState.mode  === (option.value as unknown as FbeMode))}
                                        disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('mode') )}
                                        onLock={handleLockedFields}
                                        disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                        isLocked={(formState.lockedFields || []).includes('mode')}
                                      />

                                    </InputRowContainer>
                                    <InputRowContainer>
                                    <InputField
                                      labelText={'Question set ID range'}
                                      labelId={'questionSetIdRange'}
                                      mandatory={false}
                                      helpTextTitle={dQuestionSetIDRangeHelpTitle}
                                      helpTextBody={dQuestionSetIDRangeHelpBody}
                                      onChange={(value) =>
                                        handleChange('questionSetIdRange', Number(value))
                                      }
                                      onBlur={() =>
                                        setTouchedFields((prev) => ({
                                          ...prev,
                                          questionSetIdRange: true,
                                        }))
                                      }
                                      fullWidth={true}
                                      value={formState.questionSetIdRange || ''}
                                      error={false}
                                      helperText={''}

                                      disabled={true}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                      isLocked={(formState.lockedFields || []).includes('questionSetIdRange')}
                                    />

                                  </InputRowContainer>
                                  </Box>
                              )}

                              {smallTabIndex === 2 && (
                                <Box>
                                  {/* Send Results Before */}
                                  {
                                    formState.resultHandling.map( (element, index) =>
                                      <>
                                  <Box>
                                        <FieldLock
                                          onChange={function (locked: boolean): void {
                                              handleLockedFields('resultHandling',locked);
                                          } }
                                          labelId={'resultHandling'}
                                          disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('resultHandling') )}
                                          displayed={locksDisplayed}
                                          value={(formState.lockedFields || []).includes('resultHandling')}/>
                                        Result handling {index + 1}
                                    <InputRowContainer>
                                    <MultiSelect
                                      labelText={'Generate result trigger'}
                                      labelId={'resultWorkflowStatus'}
                                      mandatory={true}
                                      fullWidth={true}
                                      helpTextTitle={sarGenerateResultTitle}
                                      helpTextBody={sarGenerateResultBody}
                                      onChange={(value) => {
                                        handleResultHandlingChange(
                                          'resultWorkflowStatus',
                                          value,
                                          index
                                        )
                                      }}
                                      options={filterResultTriggers(element, index)}
                                      selectedValues={filterResultTriggers(element, index).filter(option => `${element.resultWorkflowStatus}`.includes(option.value))}
                                      disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('resultHandling') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                      displayedLock={false}
                                      isLocked={(formState.lockedFields || []).includes('resultHandling')}
                                      showWorkflowInfo={true}
                                    />
                                  </InputRowContainer>
                                  <InputRowContainer>
                                    <MultiSelect
                                      labelText={'Result feature(s)'}
                                      labelId={'resultSystemDestination'}
                                      mandatory={false}
                                      fullWidth={true}
                                      helpTextTitle={sarUseDatabaseTitle}
                                      helpTextBody={sarUseDatabaseBody}
                                      onChange={(value) => {
                                        handleResultHandlingChange(
                                          'resultSystemDestination',
                                          value,
                                          index
                                        )
                                      }}
                                      options={resultSystemDestinationOptions}
                                      selectedValues={resultSystemDestinationOptions.filter(option => `${element.resultSystemDestination}`.includes(option.value))}
                                      disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('resultHandling') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                displayedLock={false}
                                      isLocked={(formState.lockedFields || []).includes('resultHandling')}
                                    />
                                  </InputRowContainer>

                                  {/* Results Receivers */}
                                  <InputRowContainer>
                                  <MultiSelectFree
                                    labelText={'Receiver(s)'}
                                    labelId={'receivers'}
                                    mandatory={true}
                                    fullWidth={true}
                                    helpTextTitle={sarResultReceiverTitle}
                                    helpTextBody={sarResultReceiverBody}
                                    onChange={(value) => {
                                      handleResultHandlingChange(
                                        'resultEmailReceivers',
                                        value,
                                        index
                                      )
                                    }}
                                    options={generateReceiverOptions(element.resultEmailReceivers || [])}
                                    selectedValues={generateReceiverOptions(element.resultEmailReceivers || []).filter(option => `${element.resultEmailReceivers}`.includes(option.value))}
                                    error={
                                      touchedFields.resultEmailReceivers &&
                                      !element.resultEmailReceivers
                                        ?.length
                                    }
                                    helperText={
                                      touchedFields.resultEmailReceivers &&
                                      !element.resultEmailReceivers
                                        ?.length
                                        ? 'Receivers required'
                                        : ''
                                    }
                                    disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('resultHandling') )}
                                    onLock={handleLockedFields}
                                    disabledLock={locksDisabled}
                                displayedLock={false}
                                    isLocked={(formState.lockedFields || []).includes('resultHandling')}
                                  />
                                    </InputRowContainer>
                                    <InputRowContainer>
                                    { index > 0 ? (
                                    <LineAction
                                        action={LineActionType.REMOVE}
                                        onClick={() =>
                                          handleRemoveResultHandling(
                                            index
                                            )
                                          }
                                        actionText={'Remove this result handling'}
                                        helpTextTitle={edRemoveResultHandlingTitle}
                                        helpTextBody={edRemoveResultHandlingBody}
                                        labelId={`${index}Remove this result handling`}
                                        disabled={readOnlyForm}
                                      />
                                        ): (<></>)}
                                    </InputRowContainer>
                                    </Box>
                                    </>
                                  )}
                                    <InputRowContainer>
                                            <LineAction
                                              action={LineActionType.ADD}
                                              onClick={() =>
                                                handleAddResultHandling()
                                              }
                                              actionText={'Add new result handling'}
                                              helpTextTitle={fdAddResultHandlingTitle}
                                              helpTextBody={fdAddResultHandlingBody}
                                              labelId={'Add new result handling'}
                                              disabled={readOnlyForm}
                                            />
                                      </InputRowContainer>
                                  </Box>
                              )}

                              {smallTabIndex === 3 && (
                                <Box >
                                  {/* Scoring */}

                                  {/* Form Scoring Options */}
                                  <InputRowContainer>
                                  <InputField
                                      labelText={'Pass percentage'}
                                      labelId={'passingScorePercentage'}
                                      mandatory={isScoringEnabled ? true: false}
                                      helpTextTitle={sarPercentPassTitle}
                                      helpTextBody={sarPercentPassBody}
                                      onChange={(value) =>
                                        handleChange('passingScorePercentage', Number(value))
                                      }
                                      onBlur={() =>
                                        setTouchedFields((prev) => ({
                                          ...prev,
                                          passingScorePercentage: true,
                                        }))
                                      }
                                      fullWidth={false}
                                      value={ formState.passingScorePercentage || 0}
                                      error={
                                        touchedFields.passingScorePercentage && (
                                        ((formState.passingScorePercentage || 0) < 0 ) ||
                                        ((formState.passingScorePercentage || 0) > 100 ) )
                                      }
                                      helperText={''}
                                      type="number"

                                      disabled={readOnlyForm || !isScoringEnabled || (locksDisabled && (formState.lockedFields || []).includes('passingScorePercentage') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                      isLocked={(formState.lockedFields || []).includes('passingScorePercentage')}
                                    />
                                    <FBCheckbox
                                      checked={isScoringEnabled || false}
                                      labelText={'Enable scoring'}
                                      labelId={'Enable scoring'}
                                      mandatory={false}
                                      helpTextTitle={sarUseScoringTitle}
                                      helpTextBody={sarUseScoringBody}
                                      onChange={(checked) =>
                                        handleSetIsScoringEnabled(checked)
                                      }
                                      disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('passingScorePercentage') )}
                                      onLock={handleLockedFields}
                                      disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                      isLocked={(formState.lockedFields || []).includes('passingScorePercentage')}
                                    />
                                  </InputRowContainer>
                                  </Box>
                                )}

                              {smallTabIndex === 4 && (
                                <Box >
                                  {/* Criteria */}
                                  {
                                    criteriaInfoList.map(criteriaInfo =>
                                      <>
                                      <InputRowContainer>
                                      <Box>
                                        <FieldLock
                                          onChange={function (locked: boolean): void {
                                              handleLockedFields('criteria',locked);
                                          } }
                                          labelId={'criteria'}
                                          disabled={locksDisabled}
                                  displayed={locksDisplayed}
                                          value={(formState.lockedFields || []).includes('criteria')}/>
                                        {criteriaInfo.title}</Box>
                                      </InputRowContainer>
                                      {
                                        criteriaInfo.list.map( (element, index) =>
                                          <>
                                            <InputRowContainer>
                                            <InputField
                                              labelText={'Source'}
                                              labelId={`criteriaName-${criteriaInfo.index}-${index}`}
                                              mandatory={true}
                                              helpTextTitle={fdSourceTitle}
                                              helpTextBody={fdSourceBody}
                                              onChange={(value) =>
                                                handleChange('criteria',
                                                  `A${value}`,
                                                  'en',
                                                  criteriaInfo.index,
                                                  index
                                                )
                                              }
                                              onBlur={() =>
                                                setTouchedFields((prev) => ({
                                                  ...prev,
                                                  criteria: true,
                                                }))
                                              }
                                              fullWidth={true}
                                              value={element.characteristicName || ''}
                                              error={false}
                                              helperText={''}

                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('criteria') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                              isLocked={(formState.lockedFields || []).includes('criteria')}
                                            />

                                            <InputField
                                              labelText={'Value'}
                                              labelId={`criteriaValue-${criteriaInfo.index}-${index}`}
                                              mandatory={true}
                                              helpTextTitle={fdValueTitle}
                                              helpTextBody={fdValueBody}
                                              onChange={(value) =>
                                                handleChange('criteria',
                                                  `B${value}`,
                                                  'en',
                                                  criteriaInfo.index,
                                                  index
                                                )
                                              }
                                              onBlur={() =>
                                                setTouchedFields((prev) => ({
                                                  ...prev,
                                                  criteria: true,
                                                }))
                                              }
                                              fullWidth={true}
                                              value={element.characteristicValue || ''}
                                              error={false}
                                              helperText={''}

                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('criteria') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                displayedLock={locksDisplayed}
                                              isLocked={(formState.lockedFields || []).includes('criteria')}
                                            />
                                            </InputRowContainer>
                                            <InputRowContainer>
                                            <SingleSelect
                                              labelText={'Operator'}
                                              labelId={`criteriaOperator-${criteriaInfo.index}-${index}`}
                                              mandatory={false}
                                              fullWidth={true}
                                              helpTextTitle={'Operator (optional)'}
                                              helpTextBody={'Select an operator for value comparison.<><>Equals: source must match value exactly.<><>Includes: source must contain value.<><>Different: source must not match value.<><>No operator: source must match value exactly (default).'}
                                              onChange={(value) => {
                                                handleChange(
                                                  'criteria',
                                                  `C${value}`,
                                                  'en',
                                                  criteriaInfo.index,
                                                  index
                                                )
                                              }}
                                              options={criteriaOperatorOptions}
                                              selectedValues={criteriaOperatorOptions.filter(option => `${element.operator || ''}` === option.value)}
                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('criteria') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('criteria')}
                                            />
                                            </InputRowContainer>
                                            <LineAction
                                              action={LineActionType.REMOVE}
                                              onClick={() =>
                                                handleRemoveCriteriaListData(
                                                  criteriaInfo.index,
                                                  index
                                                  )
                                                }
                                              actionText={'Remove this criteria'}
                                              helpTextTitle={edRemoveCriteriaTitle}
                                              helpTextBody={edRemoveCriteriaBody}
                                              labelId={`${criteriaInfo.index}-${index}Remove this criteria`}
                                              disabled={readOnlyForm}
                                            />
                                            <div
                                            style={{
                                              paddingLeft: '16px',
                                              paddingRight: '16px',
                                              height: '20px',
                                            }}
                                            >
                                          <hr
                                              style={{
                                                border: '0',
                                                borderTop: '1px solid #d3d3d3',
                                                margin: 0,
                                                width: '100%',
                                              }}
                                          />
                                          </div>
                                        </>
                                      )
                                      }
                                      <InputRowContainer>
                                        { criteriaInfo.list.length === 0 ? (
                                          <Box height={'18px'}></Box>
                                        ) : (<></>)
                                        }
                                            <LineAction
                                              action={LineActionType.ADD}
                                              onClick={() =>
                                                handleAddCriteriaToList(criteriaInfo.index)
                                              }
                                              actionText={'Add new criteria'}
                                              helpTextTitle={fdAddTitle}
                                              helpTextBody={fdAddBody}
                                              labelId={`${criteriaInfo.index}Add new criteria`}
                                              disabled={readOnlyForm}
                                            />
                                      </InputRowContainer>
                                      </>
                                    )
                                  }

                              </Box>
                              )}
                              {/* External data */}
                              {smallTabIndex === 5 && (
                                  <Box>
                              {formState.externalData?.map(
                                    (data, externalIndex) => (
                                      <Box key={externalIndex} >
                                        <FieldLock
                                          onChange={function (locked: boolean): void {
                                              handleLockedFields('externalData',locked);
                                          } }
                                          labelId={'externalData'}
                                          disabled={locksDisabled}
                                          displayed={locksDisplayed}
                                          value={(formState.lockedFields || []).includes('externalData')}/>
                                        {externalDataInfoList[externalIndex].title}
                                        {data.mapping.map(
                                          (mapping, mappingIndex) => (
                                            <>
                                            <InputRowContainer>
                                            <InputField
                                              labelText={'Source'}
                                              labelId={'mapSource'}
                                              mandatory={true}
                                              helpTextTitle={edSourceTitle}
                                              helpTextBody={edSourceBody}
                                              onChange={(value) =>
                                                handleChange('externalData',
                                                  value,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'source'
                                                )
                                              }
                                              onBlur={() =>
                                                setTouchedFields((prev) => ({
                                                  ...prev,
                                                  externalData: true,
                                                }))
                                              }
                                              fullWidth={true}
                                              value={mapping.source || ''}
                                              error={false}
                                              helperText={''}

                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            <InputField
                                              labelText={'Destination'}
                                              labelId={'mapDestination'}
                                              mandatory={true}
                                              helpTextTitle={edDestinationTitle}
                                              helpTextBody={edDestinationBody}
                                              onChange={(value) =>
                                                handleChange('externalData',
                                                  value,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'dest'
                                                )
                                              }
                                              onBlur={() =>
                                                setTouchedFields((prev) => ({
                                                  ...prev,
                                                  externalData: true,
                                                }))
                                              }
                                              fullWidth={true}
                                              value={mapping.dest || ''}
                                              error={false}
                                              helperText={''}

                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            </InputRowContainer>
                                            <InputRowContainer>
                                            <SingleSelect
                                              labelText={'Type'}
                                              labelId={'mapType'}
                                              mandatory={true}
                                              fullWidth={true}
                                              helpTextTitle={edTypeTitle}
                                              helpTextBody={edTypeBody}
                                              onChange={(value) => {
                                                handleChange(
                                                  'externalData',
                                                  value,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'type'
                                                )
                                              }}
                                              options={externalTypeOptions}
                                              selectedValues={externalTypeOptions.filter(option => `${getExternalDataType(externalIndex, mappingIndex)}` === option.value)}
                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            <SingleSelect
                                              labelText={'Operator'}
                                              labelId={'mapOperator'}
                                              mandatory={false}
                                              fullWidth={true}
                                              helpTextTitle={edOperatorTitle}
                                              helpTextBody={edOperatorBody}
                                              onChange={(value) => {
                                                handleChange(
                                                  'externalData',
                                                  value,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'operator'
                                                )
                                              }}
                                              options={externalTypeOperatorOptions}
                                              selectedValues={externalTypeOperatorOptions.filter(option => `${getExternalDataOperator(externalIndex, mappingIndex)}` === option.value)}
                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            </InputRowContainer>
                                            <InputRowContainer>
                                            <InputField
                                              labelText={'Compare Value'}
                                              labelId={'mapCompareValue'}
                                              mandatory={false}
                                              helpTextTitle={edCompareTitle}
                                              helpTextBody={edCompareBody}
                                              onChange={(value) =>
                                                handleChange('externalData',
                                                  value,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'compareValue'
                                                )
                                              }
                                              onBlur={() =>
                                                setTouchedFields((prev) => ({
                                                  ...prev,
                                                  externalData: true,
                                                }))
                                              }
                                              fullWidth={true}
                                              value={mapping.compareValue || ''}
                                              error={false}
                                              helperText={''}

                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            <InputField
                                              labelText={'Default'}
                                              labelId={'mapDefault'}
                                              mandatory={false}
                                              helpTextTitle={edDefaultTitle}
                                              helpTextBody={edDefaultBody}
                                              onChange={(value) =>
                                                handleChange('externalData',
                                                  value,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'default'
                                                )
                                              }
                                              onBlur={() =>
                                                setTouchedFields((prev) => ({
                                                  ...prev,
                                                  externalData: true,
                                                }))
                                              }
                                              fullWidth={true}
                                              value={mapping.default || ''}
                                              error={false}
                                              helperText={''}

                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            </InputRowContainer>
                                            <InputRowContainer>
                                            <FBCheckbox
                                              checked={mapping.isFormLevelUsage || false}
                                              labelText={'For form-level usage'}
                                              labelId={`isFormLevelUsage_${externalIndex}_${mappingIndex}`}
                                              mandatory={false}
                                              helpTextTitle={'Form-level usage'}
                                              helpTextBody={data.table === ExternalDataTable.NETWORK
                                                ? 'When checked, this network field must exist in the SAP data in order for this form to be generated. If the network field is not found, the system falls back to country-specific or global forms.'
                                                : 'When checked, this characteristic must exist in the SAP data in order for this form to be generated. If the characteristic is not found, the system falls back to country-specific or global forms.'}
                                              onChange={(checked) =>
                                                handleChange('externalData',
                                                  checked,
                                                  'en',
                                                  externalIndex,
                                                  mappingIndex,
                                                  'isFormLevelUsage'
                                                )
                                              }
                                              disabled={readOnlyForm || (locksDisabled && (formState.lockedFields || []).includes('externalData') )}
                                              onLock={handleLockedFields}
                                              disabledLock={locksDisabled}
                                              displayedLock={false}
                                              isLocked={(formState.lockedFields || []).includes('externalData')}
                                            />
                                            </InputRowContainer>
                                            <InputRowContainer>
                                            <LineAction
                                              action={LineActionType.REMOVE}
                                              onClick={() =>
                                                handleRemoveMapping(
                                                  externalIndex,
                                                  mappingIndex
                                                )
                                              }
                                                actionText={'Remove this mapping'}
                                              helpTextTitle={edRemoveMapTitle}
                                              helpTextBody={edRemoveMapBody}
                                              labelId={`${externalIndex}Remove this mapping`}
                                              disabled={readOnlyForm}
                                            />
                                            </InputRowContainer>

                                            { mappingIndex < data.mapping.length ? (
                                            <div
                                            style={{
                                              paddingLeft: '16px',
                                              paddingRight: '16px',
                                              height: '20px',
                                            }}
                                            >
                                          <hr
                                              style={{
                                                border: '0',
                                                borderTop: '1px solid #d3d3d3',
                                                margin: 0,
                                                width: '100%',
                                              }}
                                          />
                                          </div>) : <></>
                                            }
                                            </>
                                            )
                                        )}
                                            <LineAction
                                              action={LineActionType.ADD}
                                              onClick={() =>
                                                handleAddMapping(externalIndex)
                                              }
                                              actionText={'Add new mapping'}
                                              helpTextTitle={edAddMapTitle}
                                              helpTextBody={edAddMapBody}
                                              labelId={`${externalIndex}Add new mapping`}
                                              disabled={readOnlyForm}
                                            />
                                      </Box>
                                    )
                                  )}
                                </Box>
                              )}
                              { !formState.externalData || formState.externalData.length === 0 ? (
                                            <LineAction
                                            action={LineActionType.ADD}
                                            onClick={() =>
                                              handleAddMapping(0)
                                            }
                                            actionText={'Add new mapping'}
                                            helpTextTitle={edAddMapTitle}
                                            helpTextBody={edAddMapBody}
                                            labelId={'0Add new mapping'}
                                            disabled={readOnlyForm}
                                          />
                              ):<></>
                              }
                            </EditContainer>
                          </Box>
                          <GapStyled
                            mt={3}
                            display="flex"
                            flexWrap="wrap"
                            justifyContent="flex-end"
                          >
                            <ButtonStyled
                              variant="outlined"
                              onClick={() => {
                                navigate('/');
                              }}
                            >
                              Cancel
                            </ButtonStyled>
                            <ButtonStyled
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                if(isEditPath || isSaved){
                                  handleEditChanges()
                                } else {
                                  handlePostChanges();
                                }
                              }}
                              disabled={
                                !formValid || !changesMade
                              }
                            >
                              Save
                            </ButtonStyled>
                          </GapStyled>
                        </>
                      )}
                    </Box>
                    <TabContainerStyled mt={3}>
                      {tabIndex === 1 && (
                        <>
                          {restoredDraft && (
                            <Alert
                              severity="info"
                              sx={{ mb: 2 }}
                              onClose={() => setRestoredDraft(null)}
                            >
                              Your unsaved translations were restored after the reload. Review and save when ready.
                            </Alert>
                          )}
                          <FormTranslationsTab
                            key={formState.pk && formState.sk ? `${formState.pk}:${formState.sk}` : 'loading'}
                            formState={formState}
                            supportedLanguages={supportedLanguages}
                            frontline={frontline}
                            onSave={handleTranslationSave}
                            readOnly={!isLocalAdmin && readOnlyForm}
                            isAdmin={!readOnlyForm}
                            selectedLanguage={language}
                            onDefaultLanguageChange={(lang) => handleChange('defaultLanguage', lang)}
                            initialDraftName={restoredDraft?.name}
                            initialDraftDescriptions={restoredDraft?.descriptions}
                            onDirtyChange={(lang, name, description) => {
                              const savedName = getLocalizedText(formState.name || [], lang);
                              const savedDesc = lang === (formState.defaultLanguage || 'en')
                                ? (formState.description || getLocalizedText(formState.descriptions || [], lang) || '')
                                : getLocalizedText(formState.descriptions || [], lang);
                              setTranslationsDirty((name || description) ? (name !== savedName || description !== savedDesc) : false);
                            }}
                            onDraftChange={(lang, name, description) => {
                              setTranslationDraft({ lang, name, description });
                              // Keep the form-level localization in sync with the
                              // translation target language so sections auto-select it.
                              if (lang && lang !== language) {
                                setLanguage(lang);
                              }
                            }}
                          />
                        </>
                      )}
                      {tabIndex === 2 && (
                        <>
                          <FormPreview
                            form={formState}
                            sections={formSections}
                            parentForm={parentForm}
                            parentSections={parentSections}
                            language={language}
                          />
                        </>
                      )}
                      <ConfirmDialog
                        open={confirmOpen}
                        title={'Unsaved translations'}
                        content={'You have unsaved translations. Are you sure you want to leave without saving?'}
                        onConfirm={() => {
                          setConfirmOpen(false);
                          const lang = translationDraft?.lang || language;
                          const savedName = getLocalizedText(formState.name || [], lang);
                          const savedDesc = lang === (formState.defaultLanguage || 'en')
                            ? (formState.description || getLocalizedText(formState.descriptions || [], lang) || '')
                            : getLocalizedText(formState.descriptions || [], lang);
                          setTranslationDraft({ lang, name: savedName, description: savedDesc });
                          setTranslationsDirty(false);
                          if (pendingTab !== null) {
                            setTabIndex(pendingTab);
                            setPendingTab(null);
                          }
                        }}
                        onCancel={() => setConfirmOpen(false)}
                      />
                    </TabContainerStyled>
                  </Grid>
                )}
              {selectedSectionIndex !== null &&
                (selectedQuestionIndex === null ||
                  selectedQuestionIndex === -1) &&
                selectedSectionIndex !== -1 &&
                // selectedQuestionIndex !== -1 &&
                formSections &&
                selectedSectionIndex <= formSections.length && (
                  <EditSection
                    imageList={imageList}
                    frontline={frontline ? frontline : undefined}
                    language={language}
                    supportedLanguages={supportedLanguages}
                    key={selectedSectionIndex}
                    form={formResponse}
                    allForms={allFormsList}
                    formSections={formSections}
                    parentForm={parentForm}
                    parentSections={parentSections}
                    section={formSections[selectedSectionIndex]}
                    {...(originalFormSections && {
                      originalSection:
                        originalFormSections[selectedSectionIndex],
                    })}
                    readOnly={readOnly}
                    translationsReadOnly={!isLocalAdmin && readOnly}
                    sectionNumber={selectedSectionIndex}
                    onUpdateSection={(updatedSection) => {
                      const updatedSections = [...formSections];
                      updatedSections[selectedSectionIndex] = updatedSection;
                      setFormSections(updatedSections);
                    }}
                    onSubmitAllSections={handleSubmitAllSections}
                    initialTab={sectionInitialTab}
                  />
                )}
              {selectedSectionIndex !== null &&
                selectedQuestionIndex !== null &&
                // sectionIndex >= 0 &&
                formSections &&
                selectedSectionIndex !== -1 &&
                selectedQuestionIndex !== -1 && (
                  <>
                    <EditQuestion
                      frontline={frontline ? frontline : undefined}
                      language={language}
                      supportedLanguages={supportedLanguages}
                      sectionIndex={selectedSectionIndex}
                      imageList={imageList}
                      key={selectedQuestionIndex}
                      readOnly={readOnly}
                      formSections={formSections}
                      form={formResponse}
                      question={
                        formSections
                          ? formSections[selectedSectionIndex].questions[
                              selectedQuestionIndex
                            ]
                          : []
                      }
                      originalQuestion={
                        originalFormSections &&
                        originalFormSections?.length > 0 &&
                        originalFormSections[selectedSectionIndex]
                          ? originalFormSections[selectedSectionIndex]
                              .questions[selectedQuestionIndex]
                          : []
                      }
                      onUpdateQuestion={handleUpdateQuestion}
                      onSubmitAllSections={handleSubmitAllSections}
                      initialTab={questionInitialTab}
                    />
                  </>
                )}
            </Grid>
          </Container>

      {/* ── Save-conflict blocking dialog ── */}
      <Dialog open={Boolean(saveConflict)} onClose={() => setSaveConflict(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Form updated by another user</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{saveConflict?.modifiedByName || 'Another user'}</strong> saved changes to this form after you opened it.
            Your save has been blocked to prevent overwriting their work.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Please reload the page to get the latest version before saving your changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveConflict(null)}>Dismiss</Button>
          <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
            Reload now
          </Button>
        </DialogActions>
      </Dialog>
        </>
  )
}
